<template>
  <div class="report-data-board">
    <section class="hero-band">
      <div class="hero-main">
        <div class="hero-copy">
          <h1>埋点数据看版</h1>
          <p>围绕项目维度聚合流量、行为与后续模块页面数据，支持趋势分析、环比对比与前端 Excel 导出。</p>
        </div>
      </div>
      <div class="hero-meta">
        <span>项目：{{ currentProject ? `${currentProject.name} (${currentProject.code})` : '-' }}</span>
        <span>时间范围：{{ activeDateText }}</span>
        <span>对比窗口：{{ compareEnabled ? compareDateText : '未开启' }}</span>
      </div>
    </section>

    <transition name="board-fade" mode="out-in">
      <section v-if="viewMode === 'list'" class="project-browser">
        <div class="panel-header">
          <div>
            <h3>项目列表</h3>
            <p>先看项目整体情况，再点击卡片进入看板分析。卡片里也预留了埋点检测和后续项目导出入口。</p>
          </div>
          <el-button plain size="small" @click="recordDrawerVisible = true">模板与导出</el-button>
        </div>
        <div class="project-toolbar">
          <el-input v-model="projectQuery.keyword" clearable placeholder="搜索项目名称 / code" style="width: 320px"
            @keyup.enter="searchProjects" />
          <el-select v-model="projectQuery.pageSize" style="width: 140px" @change="searchProjects">
            <el-option :value="9" label="9 条/页" />
            <el-option :value="18" label="18 条/页" />
            <el-option :value="36" label="36 条/页" />
          </el-select>
          <el-button @click="searchProjects">搜索</el-button>
          <el-button @click="resetProjectQuery">重置</el-button>
          <el-switch v-model="projectQuery.onlyRunning" inline-prompt active-text="仅运行中" inactive-text="全部"
            @change="handleProjectRunningFilterChange" />
          <el-segmented v-model="projectQuery.version" :options="projectVersionOptions" @change="searchProjects" />
          <el-select v-model="projectQuery.sortField" style="width: 160px" @change="searchProjects">
            <el-option label="按创建顺序" value="id" />
            <el-option label="按 PV" value="pv" />
            <el-option label="按 UV" value="uv" />
            <el-option label="按近3天UV" value="minUv3d" />
            <el-option label="按更新时间" value="updateAt" />
          </el-select>
          <el-segmented v-model="projectQuery.sortOrder" :options="sortOrderOptions" @change="searchProjects" />
        </div>
        <div class="project-card-grid" v-loading="projectLoading">
          <article v-for="item in displayProjectOptions" :key="item.id" class="project-card" :class="{
            'is-current': item.id === selectedProjectId,
            'is-switching': item.id === switchingProjectId
          }" @click="selectProject(item)">
            <div class="project-card-head">
              <div class="project-card-title">
                <h4>{{ item.name }}</h4>
                <p>{{ item.code }}</p>
              </div>
              <div class="project-card-tags">
                <el-tag size="small" effect="plain"
                  :type="normalizeVersion(item.version) === 'V3' ? 'warning' : 'primary'">
                  {{ normalizeVersion(item.version) }}
                </el-tag>
                <el-tag size="small" :type="item.running ? 'success' : 'info'">
                  {{ item.running ? '运行中' : '未运行' }}
                </el-tag>
              </div>
            </div>

            <div class="project-card-metrics">
              <div class="project-metric-item">
                <span>PV</span>
                <strong>{{ formatNumber(item.pv) }}</strong>
              </div>
              <div class="project-metric-item">
                <span>UV</span>
                <strong>{{ formatNumber(item.uv) }}</strong>
              </div>
              <div class="project-metric-item">
                <span>近3天最小UV</span>
                <strong>{{ formatNumber(item.minUv3d) }}</strong>
              </div>
            </div>

            <div class="project-card-extra">
              <span>创建时间</span>
              <strong>{{ item.createdAt || '-' }}</strong>
            </div>

            <div class="project-card-actions">
              <el-button type="primary" :loading="switchingProjectId === item.id" @click.stop="selectProject(item)">
                {{ switchingProjectId === item.id ? '进入中' : '进入看板' }}
              </el-button>
              <el-button plain :loading="trackingCheckLoadingId === item.id" @click.stop="openTrackingCheck(item)">
                检测埋点
              </el-button>
              <el-button plain @click.stop="openProjectExport(item)">项目导出</el-button>
            </div>
          </article>
        </div>
        <div v-if="!projectLoading && !displayProjectOptions.length" class="project-card-empty">
          <el-empty description="当前筛选条件下没有项目数据" />
        </div>
        <div class="project-pagination">
          <el-pagination v-model:current-page="projectQuery.page" v-model:page-size="projectQuery.pageSize" background
            layout="total, prev, pager, next" :total="projectTotal" @current-change="searchProjects" />
        </div>
      </section>
    </transition>

    <transition name="board-fade">
      <section v-if="currentProject && viewMode === 'board'" class="sticky-action-bar">
        <div class="sticky-project">
          <el-button plain size="default" class="back-project-button" @click="backToProjectList">返回项目列表</el-button>
        </div>
        <div class="sticky-inline-filters">
          <div class="sticky-filter-chip channel-chip">
            <span>渠道</span>
            <el-select v-model="selectedChannel" clearable size="small" placeholder="全部渠道" style="width: 180px"
              @change="handleTopChannelChange">
              <el-option label="全部渠道" value="" />
              <el-option v-for="item in channelOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </div>
          <div class="sticky-filter-chip">
            <span>对比</span>
            <el-switch v-model="compareEnabled" />
            <small>{{ compareEnabled ? compareDateText : '关闭' }}</small>
          </div>
          <div class="sticky-filter-chip theme-chip">
            <span>主题</span>
            <el-select v-model="activeTheme" size="small" style="width: 112px">
              <el-option v-for="item in themeOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </div>
        </div>
        <div class="sticky-date">
          <el-date-picker v-model="dateRange" type="daterange" unlink-panels value-format="YYYY-MM-DD"
            range-separator="~" start-placeholder="开始日期" end-placeholder="结束日期" :shortcuts="datePickerShortcuts"
            style="width: 320px" />
        </div>
        <div class="sticky-actions">
          <el-button size="small" plain :loading="trackingCheckLoadingId === currentProject.id"
            @click="openTrackingCheck(currentProject)">检测</el-button>
          <el-button size="small" plain @click="openExportDrawer" :disabled="!currentProject">导出</el-button>
          <el-button size="small" type="primary" :loading="loading" @click="refreshAll">刷新</el-button>
        </div>
      </section>
    </transition>

    <section v-if="!currentProject && viewMode === 'board'" class="empty-project-shell">
      <el-empty description="请先从上方项目列表选择一个项目，再查看数据看板" />
    </section>

    <template v-if="currentProject && viewMode === 'board'">
      <section class="kpi-grid" v-loading="trafficLoading">
        <article v-for="item in trafficKpis" :key="item.key" class="kpi-card">
          <div class="kpi-head">
            <span>{{ item.label }}</span>
            <el-tag size="small" effect="plain" :type="item.tagType">{{ item.tag }}</el-tag>
          </div>
          <p class="kpi-value">{{ item.value }}</p>
          <p class="kpi-compare" :class="deltaClass(item.delta)">
            {{ formatDeltaText(item.delta, item.percent) }}
          </p>
          <p class="kpi-sub">{{ item.sub }}</p>
        </article>
      </section>

      <el-tabs v-model="activeTab" class="board-tabs">
        <el-tab-pane label="流量数据" name="traffic" lazy>
          <section class="panel-grid two-up">
            <div class="panel-shell" v-loading="trafficLoading">
              <div class="panel-header">
                <div>
                  <h3>整体趋势</h3>
                  <p>按天查看核心流量与行为转化走势</p>
                </div>
                <el-segmented v-model="trafficTrendMetric" :options="trafficMetricOptions" />
              </div>
              <div ref="trafficTrendChartRef" class="chart-box large-chart"></div>
            </div>

            <div class="panel-shell" v-loading="trafficLoading">
              <div class="panel-header">
                <div>
                  <h3>渠道分布</h3>
                  <p>当前时间范围内各渠道贡献占比</p>
                </div>
                <el-radio-group v-model="trafficChannelMetric" size="small">
                  <el-radio-button label="pv">PV</el-radio-button>
                  <el-radio-button label="uv">UV</el-radio-button>
                </el-radio-group>
              </div>
              <div ref="trafficChannelChartRef" class="chart-box medium-chart"></div>
            </div>
          </section>

          <section class="panel-grid split-grid">
            <div class="panel-shell" v-loading="trafficLoading">
              <div class="panel-header">
                <div>
                  <h3>渠道排行</h3>
                  <p>支持勾选导出渠道，便于做横向比较</p>
                </div>
                <el-button text @click="openExportDrawer">配置导出渠道</el-button>
              </div>
              <el-table :data="trafficChannelRows" stripe height="360">
                <el-table-column type="index" width="56" label="#" />
                <el-table-column prop="channel" label="渠道" min-width="220" />
                <el-table-column prop="pv" label="PV" min-width="110">
                  <template #default="{ row }">{{ formatNumber(row.pv) }}</template>
                </el-table-column>
                <el-table-column prop="uv" label="UV" min-width="110">
                  <template #default="{ row }">{{ formatNumber(row.uv) }}</template>
                </el-table-column>
                <el-table-column label="PV占比" min-width="110">
                  <template #default="{ row }">{{ formatPercent(calcRate(row.pv, trafficCurrent.pv)) }}</template>
                </el-table-column>
                <el-table-column label="UV占比" min-width="110">
                  <template #default="{ row }">{{ formatPercent(calcRate(row.uv, trafficCurrent.uv)) }}</template>
                </el-table-column>
              </el-table>
            </div>

            <div class="panel-shell" v-loading="trafficLoading">
              <div class="panel-header">
                <div>
                  <h3>导出预设</h3>
                  <p>流量与行为数据导出参数可快速切换</p>
                </div>
              </div>
              <div class="export-summary">
                <div class="summary-item">
                  <span>当前 Sheet 数</span>
                  <strong>{{ exportSheets.length || 0 }}</strong>
                </div>
                <div class="summary-item">
                  <span>日期方向</span>
                  <strong>{{ exportForm.orientation === 'date-row' ? '日期做表头' : '日期做列头' }}</strong>
                </div>
                <div class="summary-item">
                  <span>指标范围</span>
                  <strong>{{ metricModeLabel }}</strong>
                </div>
                <div class="summary-item">
                  <span>Excel 风格</span>
                  <strong>{{ exportThemeLabel }}</strong>
                </div>
              </div>
              <el-button type="primary" style="width: 100%" @click="openExportDrawer">打开导出配置</el-button>
            </div>
          </section>

          <section class="panel-shell table-panel" v-loading="trafficLoading">
            <div class="panel-header">
              <div>
                <h3>按天明细</h3>
                <p>展示每天的数值、单日环比和转化率</p>
              </div>
            </div>
            <el-table :data="trafficDailyRows" stripe height="420">
              <el-table-column prop="date" label="日期" min-width="120" fixed />
              <el-table-column prop="pv" label="PV" min-width="110">
                <template #default="{ row }">
                  <div class="metric-cell">
                    <span>{{ formatNumber(row.pv) }}</span>
                    <small :class="deltaClass(row.pvMom)">{{ formatMom(row.pvMom) }}</small>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="uv" label="UV" min-width="110">
                <template #default="{ row }">
                  <div class="metric-cell">
                    <span>{{ formatNumber(row.uv) }}</span>
                    <small :class="deltaClass(row.uvMom)">{{ formatMom(row.uvMom) }}</small>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="clickUser" label="点击人数" min-width="120">
                <template #default="{ row }">
                  <div class="metric-cell">
                    <span>{{ formatNumber(row.clickUser) }}</span>
                    <small :class="deltaClass(row.clickUserMom)">{{ formatMom(row.clickUserMom) }}</small>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="clickCount" label="点击次数" min-width="120">
                <template #default="{ row }">
                  <div class="metric-cell">
                    <span>{{ formatNumber(row.clickCount) }}</span>
                    <small :class="deltaClass(row.clickCountMom)">{{ formatMom(row.clickCountMom) }}</small>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="点击率" min-width="110">
                <template #default="{ row }">{{ formatPercent(row.clickRate) }}</template>
              </el-table-column>
              <el-table-column label="流失率" min-width="110">
                <template #default="{ row }">{{ formatPercent(row.dropRate) }}</template>
              </el-table-column>
              <el-table-column prop="avgStayTime" label="平均停留" min-width="120">
                <template #default="{ row }">{{ formatStayTime(row.avgStayTime) }}</template>
              </el-table-column>
              <el-table-column prop="sevenDayReturnUser" label="7日回访" min-width="110">
                <template #default="{ row }">{{ formatNumber(row.sevenDayReturnUser) }}</template>
              </el-table-column>
            </el-table>
          </section>
        </el-tab-pane>

        <el-tab-pane label="行为数据" name="behavior" lazy>
          <section class="filter-strip">
            <el-select v-model="selectedBehaviorName" filterable remote clearable reserve-keyword
              placeholder="搜索并选择行为，例如 商品 / 点击 / 视频" style="width: 360px"
              :loading="behaviorSearchLoading" :remote-method="handleBehaviorKeywordSearch" default-first-option
              @visible-change="handleBehaviorSelectVisibleChange" @change="handleBehaviorSelectChange"
              @clear="handleBehaviorKeywordClear">
              <el-option v-for="item in behaviorSelectOptions" :key="item.value" :label="item.label"
                :value="item.value" />
            </el-select>
            <el-button :loading="behaviorSearchLoading" @click="refreshBehavior">查询行为</el-button>
            <el-radio-group v-model="behaviorSortMetric" size="default">
              <el-radio-button label="pv">按 PV Top10</el-radio-button>
              <el-radio-button label="uv">按 UV Top10</el-radio-button>
            </el-radio-group>
          </section>

          <section class="panel-grid two-up">
            <div class="panel-shell" v-loading="behaviorDetailLoading || behaviorLoading">
              <div class="panel-header">
                <div>
                  <h3>行为按天趋势</h3>
                  <p>{{ selectedBehaviorName || '点击下方行为行查看明细趋势' }}</p>
                </div>
                <el-segmented v-model="behaviorTrendMetric" :options="trafficMetricOptions.slice(0, 2)" />
              </div>
              <div ref="behaviorTrendChartRef" class="chart-box large-chart"></div>
            </div>

            <div class="panel-shell" v-loading="behaviorDetailLoading || behaviorLoading">
              <div class="panel-header">
                <div>
                  <h3>行为渠道分布</h3>
                  <p>{{ selectedBehaviorName ? '当前选中行为的渠道占比' : '待选择行为' }}</p>
                </div>
              </div>
              <div ref="behaviorChannelChartRef" class="chart-box medium-chart"></div>
            </div>
          </section>

          <section class="panel-grid two-up">
            <div class="panel-shell" v-loading="behaviorLoading">
              <div class="panel-header">
                <div>
                  <h3>行为 Top10</h3>
                  <p>默认按当前筛选条件下的 {{ behaviorSortMetric.toUpperCase() }} 从高到低展示</p>
                </div>
              </div>
              <el-table :data="behaviorTopRows" stripe height="320" :row-class-name="topBehaviorRowClassName"
                @row-click="handleBehaviorRowClick">
                <el-table-column type="index" width="56" label="#" />
                <el-table-column prop="indexName" label="行为名称" min-width="320" show-overflow-tooltip>
                  <template #default="{ row }">
                    <div class="behavior-name-cell">
                      <span>{{ row.indexName }}</span>
                      <el-tag v-if="row.indexName === selectedBehaviorName" size="small" type="primary" effect="light">
                        当前选中
                      </el-tag>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column prop="pv" label="PV" min-width="100">
                  <template #default="{ row }">{{ formatNumber(row.pv) }}</template>
                </el-table-column>
                <el-table-column prop="uv" label="UV" min-width="100">
                  <template #default="{ row }">{{ formatNumber(row.uv) }}</template>
                </el-table-column>
                <el-table-column :label="`${behaviorSortMetric.toUpperCase()}对比`" min-width="150">
                  <template #default="{ row }">
                    <span :class="deltaClass(row[`${behaviorSortMetric}Delta`])">
                      {{ formatDeltaText(row[`${behaviorSortMetric}Delta`], row[`${behaviorSortMetric}DeltaRate`]) }}
                    </span>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <div class="panel-shell" v-loading="behaviorLoading">
              <div class="panel-header">
                <div>
                  <h3>当前分析说明</h3>
                  <p>上方输入框用于筛选行为列表；具体趋势和渠道分布请直接点击表格行查看。</p>
                </div>
              </div>
              <div class="behavior-guide-panel">
                <div class="guide-item">
                  <span>当前渠道</span>
                  <strong>{{ selectedChannel || '全部渠道' }}</strong>
                </div>
                <div class="guide-item">
                  <span>行为筛选</span>
                  <strong>{{ behaviorFilters.keyword || '全部行为' }}</strong>
                </div>
                <div class="guide-item">
                  <span>当前查看</span>
                  <strong>{{ selectedBehaviorName || '未选中行为' }}</strong>
                </div>
                <div class="guide-tip">
                  渠道切换会影响上面的整体趋势和行为趋势；渠道分布图与渠道排行始终展示全部渠道，方便横向比较。
                </div>
              </div>
            </div>
          </section>

          <section class="behavior-detail-band" v-if="selectedBehaviorSummary" v-loading="behaviorDetailLoading">
            <div class="detail-card">
              <span>行为名称</span>
              <strong>{{ selectedBehaviorSummary.indexName }}</strong>
            </div>
            <div class="detail-card">
              <span>PV</span>
              <strong>{{ formatNumber(selectedBehaviorSummary.pv) }}</strong>
            </div>
            <div class="detail-card">
              <span>UV</span>
              <strong>{{ formatNumber(selectedBehaviorSummary.uv) }}</strong>
            </div>
            <div class="detail-card">
              <span>PV变化</span>
              <strong :class="deltaClass(selectedBehaviorSummary.pvDelta)">{{
                formatDeltaText(selectedBehaviorSummary.pvDelta,
                selectedBehaviorSummary.pvDeltaRate) }}</strong>
            </div>
            <div class="detail-card">
              <span>UV变化</span>
              <strong :class="deltaClass(selectedBehaviorSummary.uvDelta)">{{
                formatDeltaText(selectedBehaviorSummary.uvDelta,
                selectedBehaviorSummary.uvDeltaRate) }}</strong>
            </div>
          </section>

          <section class="panel-shell table-panel" v-loading="behaviorLoading">
            <div class="panel-header">
              <div>
                <h3>行为列表</h3>
                <p>点击行可查看指定行为的趋势与渠道贡献；导出请在右上角抽屉里组合配置</p>
              </div>
              <div class="table-actions">
                <el-button text
                  @click="behaviorSelection = behaviorRows.slice(0, 3).map(item => item.indexName)">默认勾选前3项</el-button>
              </div>
            </div>
            <el-table :data="behaviorRows" stripe height="460" row-key="indexName"
              @selection-change="handleBehaviorSelectionChange" @row-click="handleBehaviorRowClick">
              <el-table-column type="selection" width="48" />
              <el-table-column prop="indexName" label="行为名称" min-width="360" show-overflow-tooltip />
              <el-table-column prop="pv" label="PV" min-width="110">
                <template #default="{ row }">{{ formatNumber(row.pv) }}</template>
              </el-table-column>
              <el-table-column prop="uv" label="UV" min-width="110">
                <template #default="{ row }">{{ formatNumber(row.uv) }}</template>
              </el-table-column>
              <el-table-column label="PV占比" min-width="110">
                <template #default="{ row }">{{ formatPercent(row.pvShare) }}</template>
              </el-table-column>
              <el-table-column label="UV占比" min-width="110">
                <template #default="{ row }">{{ formatPercent(row.uvShare) }}</template>
              </el-table-column>
              <el-table-column label="PV对比" min-width="150">
                <template #default="{ row }">
                  <span :class="deltaClass(row.pvDelta)">{{ formatDeltaText(row.pvDelta, row.pvDeltaRate) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="UV对比" min-width="150">
                <template #default="{ row }">
                  <span :class="deltaClass(row.uvDelta)">{{ formatDeltaText(row.uvDelta, row.uvDeltaRate) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="详情" min-width="90" fixed="right">
                <template #default="{ row }">
                  <el-button text type="primary" @click.stop="showBehaviorDetail(row)">查看</el-button>
                </template>
              </el-table-column>
            </el-table>
          </section>
        </el-tab-pane>

        <el-tab-pane label="模块数据" name="module" lazy>
          <section class="placeholder-grid">
            <div class="placeholder-shell">
              <h3>模块数据框架已就位</h3>
              <p>后续接入模块主数据、模块按天明细、模块渠道贡献后，可以直接复用当前的图表、对比和导出模式。</p>
              <ul>
                <li>顶部 KPI 区展示模块访问、点击、转化</li>
                <li>中部图表区展示模块趋势与模块贡献排行</li>
                <li>底部表格区展示模块明细与单日环比</li>
              </ul>
            </div>
          </section>
        </el-tab-pane>

        <el-tab-pane label="页面数据" name="page" lazy>
          <section class="placeholder-grid">
            <div class="placeholder-shell">
              <h3>页面数据框架已就位</h3>
              <p>页面维度建议沿用同一套交互：页面列表、选中页面趋势、页面渠道分布、页面导出预设，后面只需要挂新接口。</p>
              <ul>
                <li>适合做页面漏斗、页面跳失和页面转化链路</li>
                <li>导出结构依然支持日期横向 / 纵向切换</li>
                <li>Excel 文件名会自动带上项目、页面分类和日期区间</li>
              </ul>
            </div>
          </section>
        </el-tab-pane>
      </el-tabs>
    </template>

    <el-drawer v-model="exportDrawerVisible" title="导出配置" size="640px" @closed="resetExportDrawerState">
      <div class="export-drawer">
        <el-form label-position="top">
    <!-- 模板快速载入 -->
    <el-form-item v-if="exportDrawerProjectInfo.code">
      <div class="template-quick-load">
        <span class="template-quick-label">从模板载入</span>
        <el-select
          v-model="drawerSelectedTemplateId"
          clearable
          filterable
          placeholder="选择已有模板快速填充配置"
          style="flex:1"
          @change="handleDrawerLoadTemplate"
        >
          <el-option
            v-for="tpl in drawerTemplates"
            :key="tpl.id"
            :label="tpl.alias"
            :value="tpl.id"
          >
            <div class="template-option">
              <span>{{ tpl.alias }}</span>
              <el-tag size="small" :type="tpl.visibility === 1 ? 'success' : 'info'" effect="plain" style="margin-left:8px">
                {{ tpl.visibility === 1 ? '公开' : '个人' }}
              </el-tag>
            </div>
          </el-option>
        </el-select>
        <el-button plain size="small" @click="loadDrawerTemplates" :loading="drawerTemplatesLoading">刷新</el-button>
      </div>
      <p v-if="drawerCurrentTemplateId" class="export-form-hint">
        当前已载入模板，保存时将<strong>覆盖更新</strong>该模板的配置；如需另存为新模板请先清除选择。
      </p>
      <p v-else-if="drawerSelectedTemplateId" class="export-form-hint">已载入模板，可直接修改后导出，也可保存为新模板。</p>
    </el-form-item>

          <el-form-item label="导出时间范围">
            <el-date-picker v-model="exportDateRange" type="daterange" unlink-panels value-format="YYYY-MM-DD"
              range-separator="~" start-placeholder="开始日期" end-placeholder="结束日期" :shortcuts="datePickerShortcuts"
              style="width: 100%" />
          </el-form-item>

          <el-form-item label="日期方向">
            <el-radio-group v-model="exportForm.orientation">
              <el-radio-button label="date-row">日期做表头</el-radio-button>
              <el-radio-button label="date-column">日期做列头</el-radio-button>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="Excel 主题">
            <el-radio-group v-model="exportForm.themeKey">
              <el-radio-button v-for="theme in exportThemes" :key="theme.key" :label="theme.key">
                {{ theme.label }}
              </el-radio-button>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="汇总行配置（应用到所有 Sheet）">
            <div class="summary-row-config">
              <el-checkbox v-model="exportForm.includeSummaryRow">在数据末尾追加汇总行</el-checkbox>
              <template v-if="exportForm.includeSummaryRow">
                <div class="summary-row-options">
                  <el-checkbox v-model="exportForm.summaryDedup">汇总去重（日期范围内 UV 去重求和）</el-checkbox>
                  <el-checkbox
                    v-model="exportForm.summaryCumDaily"
                    :disabled="dateDiffDays <= 1"
                    :title="dateDiffDays <= 1 ? '日期范围仅1天，无需累加' : ''"
                  >按天累加（各天逐日累积，停留时长不参与）</el-checkbox>
                </div>
                <p class="export-form-hint">两种方式可同时勾选，各自在数据末尾追加一行；不勾则不追加。</p>
              </template>
            </div>
          </el-form-item>

          <div class="export-sheet-builder">
            <div class="panel-header">
              <div>
                <h3>新增 Sheet</h3>
                <p>流量 Sheet 支持多指标，行为 Sheet 固定输出 PV / UV。</p>
              </div>
            </div>

            <el-form-item label="Sheet 类型">
              <el-radio-group v-model="exportSheetDraft.category" @change="handleExportDraftCategoryChange">
                <el-radio-button label="traffic">流量数据</el-radio-button>
                <el-radio-button label="behavior">行为数据</el-radio-button>
              </el-radio-group>
            </el-form-item>

            <el-form-item label="Sheet 名称">
              <el-input v-model="exportSheetDraft.sheetName" placeholder="例如：整体流量、商品点击行为" />
            </el-form-item>

            <!-- ────────── 流量数据 ────────── -->
            <template v-if="exportSheetDraft.category === 'traffic'">
              <!-- 渠道与指标一体化配置 -->
              <el-form-item label="渠道与指标">
                <!-- 全局快捷操作 -->
                <div class="channel-metric-toolbar">
                  <span class="channel-metric-toolbar-label">为所有已选渠道设置：</span>
                  <el-button size="small" plain @click="setAllChannelsMetrics(ALL_METRIC_KEYS)">全部指标</el-button>
                  <el-button size="small" plain @click="setAllChannelsMetrics(DEFAULT_CHANNEL_METRICS)">PV+UV</el-button>
                  <el-button size="small" plain @click="setOverallFullOthersBasic">整体全量 / 其他 PV+UV</el-button>
                  <el-button size="small" plain @click="selectDraftChannels">全选渠道</el-button>
                  <el-button size="small" plain @click="clearDraftChannels">清空渠道</el-button>
                </div>

                <!-- 渠道列表：整体横向滚动，列对齐 -->
                <div class="channel-config-list" v-loading="exportDrawerChannelsLoading">
                  <div class="channel-config-table">
                  <!-- 表头行 -->
                  <div class="channel-config-row channel-config-header">
                    <span class="channel-name-cell">渠道</span>
                    <span
                      v-for="m in ALL_TRAFFIC_METRICS"
                      :key="m.key"
                      class="channel-metric-cell"
                    >{{ m.label }}</span>
                  </div>

                  <!-- 整体 -->
                  <div class="channel-config-row">
                    <div class="channel-name-cell">
                      <el-checkbox
                        :model-value="!!exportSheetDraft.channelConfigs.find(c => c.channel === '__overall__')"
                        @change="toggleTrafficChannel('__overall__')"
                      >整体</el-checkbox>
                    </div>
                    <template v-if="exportSheetDraft.channelConfigs.find(c => c.channel === '__overall__')">
                      <div
                        v-for="m in ALL_TRAFFIC_METRICS"
                        :key="m.key"
                        class="channel-metric-cell"
                      >
                        <el-checkbox
                          :model-value="exportSheetDraft.channelConfigs.find(c => c.channel === '__overall__')?.metrics.includes(m.key)"
                          @change="(v) => {
                            const cfg = exportSheetDraft.channelConfigs.find(c => c.channel === '__overall__')
                            if (!cfg) return
                            if (v) { cfg.metrics.push(m.key) } else { cfg.metrics = cfg.metrics.filter(k => k !== m.key) }
                          }"
                        />
                      </div>
                    </template>
                    <template v-else>
                      <div v-for="m in ALL_TRAFFIC_METRICS" :key="m.key" class="channel-metric-cell" />
                    </template>
                  </div>

                  <!-- 其他渠道 -->
                  <div
                    v-for="item in exportDrawerChannelOptions"
                    :key="item.value"
                    class="channel-config-row"
                    :class="{ 'is-active': !!exportSheetDraft.channelConfigs.find(c => c.channel === item.value) }"
                  >
                    <div class="channel-name-cell">
                      <el-checkbox
                        :model-value="!!exportSheetDraft.channelConfigs.find(c => c.channel === item.value)"
                        @change="toggleTrafficChannel(item.value)"
                      >{{ item.label }}</el-checkbox>
                    </div>
                    <template v-if="exportSheetDraft.channelConfigs.find(c => c.channel === item.value)">
                      <div
                        v-for="m in ALL_TRAFFIC_METRICS"
                        :key="m.key"
                        class="channel-metric-cell"
                      >
                        <el-checkbox
                          :model-value="exportSheetDraft.channelConfigs.find(c => c.channel === item.value)?.metrics.includes(m.key)"
                          @change="(v) => {
                            const cfg = exportSheetDraft.channelConfigs.find(c => c.channel === item.value)
                            if (!cfg) return
                            if (v) { cfg.metrics.push(m.key) } else { cfg.metrics = cfg.metrics.filter(k => k !== m.key) }
                          }"
                        />
                      </div>
                    </template>
                    <template v-else>
                      <div v-for="m in ALL_TRAFFIC_METRICS" :key="m.key" class="channel-metric-cell" />
                    </template>
                  </div>
                  </div>
                </div>
              </el-form-item>

              <el-form-item
                label="停留时长配置"
                v-if="exportSheetDraft.channelConfigs.some(c => c.metrics.includes('sumStayTime') || c.metrics.includes('avgStayTime'))"
              >
                <div class="stay-time-config-block">
                  <!-- 检测区 -->
                  <div class="stay-detect-bar">
                    <el-button
                      size="small"
                      :loading="stayDetecting"
                      @click="detectStayTimeSource"
                    >检测可用口径</el-button>
                    <div class="stay-detect-result" v-if="stayDetectResult">
                      <el-tag
                        size="small"
                        :type="stayDetectResult.hasTimeOnSite ? 'success' : 'info'"
                      >
                        timeOnSite：{{ stayDetectResult.hasTimeOnSite ? `有数据（PV ${stayDetectResult.timeOnSitePv}）` : '无数据' }}
                      </el-tag>
                      <el-tag
                        size="small"
                        :type="stayDetectResult.hasTraceId ? 'success' : 'info'"
                      >
                        traceId：{{ stayDetectResult.hasTraceId ? `有数据（记录 ${stayDetectResult.traceIdCount} 条）` : '无数据' }}
                      </el-tag>
                      <span class="stay-detect-tip">
                        推荐：<strong>{{ stayDetectResult.recommend === 'dataworks' ? 'type=timeOnSite' : 'traceId 计算' }}</strong>
                        <el-button text type="primary" size="small" @click="exportForm.stayTimeSource = stayDetectResult.recommend">应用推荐</el-button>
                      </span>
                    </div>
                  </div>

                  <div>
                    <span class="config-label">计算口径</span>
                    <el-radio-group v-model="exportForm.stayTimeSource" size="small">
                      <el-radio-button label="dataworks">type=timeOnSite</el-radio-button>
                      <el-radio-button label="traceId">traceId 计算（MAX-MIN）</el-radio-button>
                    </el-radio-group>
                  </div>
                  <div>
                    <span class="config-label">平均值口径</span>
                    <el-radio-group v-model="exportForm.stayAvgBase" size="small">
                      <el-radio-button label="visitUv">除以访问 UV</el-radio-button>
                      <el-radio-button label="stayUv">除以停留 UV</el-radio-button>
                    </el-radio-group>
                  </div>
                  <p class="export-form-hint">停留时长自动走 DataWorks 查询，不参与按天累加。</p>
                </div>
              </el-form-item>
            </template>

            <!-- ────────── 行为数据 ────────── -->
            <template v-if="exportSheetDraft.category === 'behavior'">
              <el-form-item label="导出渠道">
                <div class="export-inline-actions">
                  <el-button text @click="selectDraftChannels">全选</el-button>
                  <el-button text @click="clearDraftChannels">清空</el-button>
                </div>
                <div class="channel-scroll-wrap" v-loading="exportDrawerChannelsLoading">
                  <el-checkbox-group v-model="exportSheetDraft.channels" class="channel-scroll-group">
                    <el-checkbox label="__overall__">整体</el-checkbox>
                    <el-checkbox v-for="item in exportDrawerChannelOptions" :key="item.value" :label="item.value">{{ item.label }}</el-checkbox>
                  </el-checkbox-group>
                </div>
              </el-form-item>

              <!-- 搜索 + 工具栏 + 结果列表 一体化 -->
              <el-form-item label="行为搜索">
                <!-- 搜索框 -->
                <div class="export-search-row">
                  <el-input
                    v-model="exportSheetDraft.behaviorKeyword"
                    clearable
                    placeholder="输入关键字，例如 商品 / 点击"
                    @input="handleExportBehaviorKeywordSearch(exportSheetDraft.behaviorKeyword)"
                    @keyup.enter="runExportBehaviorSearch"
                    @clear="handleExportBehaviorKeywordClear"
                  />
                  <el-button :loading="exportBehaviorSearchLoading" @click="runExportBehaviorSearch">搜索</el-button>
                </div>

                <!-- 工具栏 -->
                <div class="behavior-toolbar">
                  <div class="behavior-toolbar-left">
                    <el-checkbox
                      :model-value="exportBehaviorOptions.length > 0 && exportBehaviorOptions.every(i => exportSheetDraft.searchSelected.includes(i.value))"
                      :indeterminate="exportSheetDraft.searchSelected.length > 0 && exportBehaviorOptions.some(i => exportSheetDraft.searchSelected.includes(i.value)) && !exportBehaviorOptions.every(i => exportSheetDraft.searchSelected.includes(i.value))"
                      @change="(v) => v ? selectAllDraftBehaviors() : (exportSheetDraft.searchSelected = [])"
                    >全选结果</el-checkbox>
                    <el-checkbox v-model="exportExcludeCurrentSheetBehaviors" size="small">排除当前Sheet已选</el-checkbox>
                    <el-checkbox v-model="exportExcludeOtherSheetBehaviors" size="small">排除其他Sheet已选</el-checkbox>
                    <el-checkbox v-model="exportExcludeMergeGroupBehaviors" size="small">排除合并组已选</el-checkbox>
                  </div>
                  <div class="behavior-toolbar-right">
                    <el-button
                      size="small"
                      type="success"
                      plain
                      :disabled="!exportSheetDraft.searchSelected.length"
                      @click="addSelectedToSheet"
                    >加入Sheet（{{ exportSheetDraft.searchSelected.length }}）</el-button>
                    <div class="merge-group-action">
                      <el-input
                        v-model="exportSheetDraft.mergeName"
                        size="small"
                        :placeholder="exportSheetDraft.editingGroupIdx !== null ? `编辑：${exportSheetDraft.mergedBehaviors[exportSheetDraft.editingGroupIdx]?.name || '组 '+(exportSheetDraft.editingGroupIdx+1)}` : '合并组名称'"
                        style="width: 150px"
                      />
                      <el-button
                        size="small"
                        type="primary"
                        plain
                        :disabled="!exportSheetDraft.searchSelected.length"
                        @click="commitMergeGroup"
                      >{{ exportSheetDraft.editingGroupIdx !== null ? '更新到组' : '合并为组' }}（{{ exportSheetDraft.searchSelected.length }}）</el-button>
                      <el-button
                        v-if="exportSheetDraft.editingGroupIdx !== null"
                        size="small"
                        plain
                        @click="cancelEditGroup"
                      >退出编辑</el-button>
                    </div>
                  </div>
                </div>

                <!-- 搜索结果列表 -->
                <div class="behavior-search-result-list" v-if="exportBehaviorOptions.length">
                  <div
                    v-for="item in exportBehaviorOptions"
                    :key="item.value"
                    class="behavior-search-result-item"
                    :class="{ 'is-selected': exportSheetDraft.searchSelected.includes(item.value) }"
                    @click="toggleDraftBehavior(item.value)"
                  >
                    <el-checkbox
                      :model-value="exportSheetDraft.searchSelected.includes(item.value)"
                      @click.stop
                      @change="toggleDraftBehavior(item.value)"
                    />
                    <span class="behavior-item-name" :title="item.label">{{ item.label }}</span>
                    <span class="behavior-item-badge" v-if="exportSheetDraft.behaviors.includes(item.value)">Sheet</span>
                    <span class="behavior-item-badge merge" v-if="item.mergeGroupLabel" :title="`已参与合并组：${item.mergeGroupLabel}`">合并组·{{ item.mergeGroupLabel }}</span>
                  </div>
                </div>
                <div v-else class="export-empty-tip" style="margin-top:8px">暂无结果，试试清空关键字</div>
              </el-form-item>

              <!-- 独立行为 -->
              <el-form-item>
                <template #label>
                  <div class="behavior-sheet-label-row">
                    <span>Sheet 独立行为</span>
                    <el-checkbox
                      v-model="exportSheetDraft.sortBehaviors"
                      size="small"
                      @change="handleSortBehaviorsChange"
                    >
                      按字母序排序
                    </el-checkbox>
                  </div>
                </template>
                <p class="export-form-hint behavior-sort-hint">
                  勾选后：添加/再次勾选会立刻重排下方列表；「加入清单」或「更新」时按此顺序写入。导出按已保存顺序，不再二次排序。未勾选则保持添加顺序（旧模板默认不勾选）。
                </p>
                <div class="behavior-tag-area">
                  <el-tag
                    v-for="item in exportSheetDraft.behaviors"
                    :key="item"
                    size="small"
                    closable
                    :title="item"
                    @close="removeDraftBehavior(item)"
                  >{{ item }}</el-tag>
                </div>
              </el-form-item>

              <!-- 合并组 -->
              <el-form-item>
                <template #label>
                  <div class="behavior-search-label">
                    <span>合并组</span>
                    <div class="merged-group-label-actions">
                      <el-checkbox
                        :model-value="exportSheetDraft.mergedBehaviors.length > 0 && exportSheetDraft.mergedBehaviors.every(m => m.keepOriginal)"
                        :indeterminate="exportSheetDraft.mergedBehaviors.some(m => m.keepOriginal) && !exportSheetDraft.mergedBehaviors.every(m => m.keepOriginal)"
                        size="small"
                        @change="(v) => exportSheetDraft.mergedBehaviors.forEach(m => m.keepOriginal = v)"
                      >全部保留原始行</el-checkbox>
                      <el-button text type="primary" size="small" @click="addMergedBehavior">+ 新建空组</el-button>
                    </div>
                  </div>
                </template>
                <div class="merged-group-list" v-if="exportSheetDraft.mergedBehaviors.length">
                  <div
                    v-for="(merged, idx) in exportSheetDraft.mergedBehaviors"
                    :key="idx"
                    class="merged-group-card"
                    :class="{ 'is-editing': exportSheetDraft.editingGroupIdx === idx }"
                    @click="handleMergeGroupCardClick(idx)"
                  >
                    <div class="merged-group-head">
                      <div class="merged-group-name-wrap">
                        <span class="merged-group-badge">组 {{ idx + 1 }}</span>
                        <el-input
                          v-model="merged.name"
                          placeholder="合并名称"
                          size="small"
                          style="width: 160px"
                          @click.stop
                          @input="exportSheetDraft.editingGroupIdx === idx && (exportSheetDraft.mergeName = merged.name)"
                        />
                      </div>
                      <div class="merged-group-right" @click.stop>
                        <el-checkbox v-model="merged.keepOriginal" size="small">保留各自原始行</el-checkbox>
                        <template v-if="exportSheetDraft.editingGroupIdx === idx">
                          <el-button text size="small" type="primary" @click="saveMergeGroupEdit">保存</el-button>
                          <el-button text size="small" @click="cancelMergeGroupEdit">取消</el-button>
                        </template>
                        <el-button v-else text size="small" type="primary" @click="handleMergeGroupCardClick(idx)">编辑</el-button>
                        <el-button text type="danger" size="small" @click="removeMergedBehavior(idx)">删除</el-button>
                      </div>
                    </div>
                    <div class="merged-group-behaviors">
                      <el-tag
                        v-for="b in getMergedGroupBehaviors(idx)"
                        :key="b"
                        size="small"
                        closable
                        :title="b"
                        @close="toggleBehaviorInMerge(idx, b)"
                      >{{ b }}</el-tag>
                      <span v-if="!merged.behaviors.length" class="export-empty-tip">组内暂无成员</span>
                    </div>
                  </div>
                </div>
                <div v-else class="export-empty-tip">勾选行为后点「合并为组」，或点右上角「+ 新建空组」</div>
              </el-form-item>
            </template>

            <!-- 已选渠道总览 -->
            <el-form-item label="已选渠道">
              <div class="sheet-selected-preview">
                <el-tag
                  v-for="item in exportSheetDraft.channels"
                  :key="item"
                  size="small"
                  closable
                  @close="removeDraftChannel(item)"
                >{{ item === '__overall__' ? '整体' : item }}</el-tag>
                <span v-if="!exportSheetDraft.channels.length" class="export-empty-tip">还没有选择渠道</span>
              </div>
            </el-form-item>

            <div class="draft-sheet-actions">
              <el-button type="primary" style="width: 100%" @click="handleDraftSubmit">
                {{ editingSheetId ? '更新此 Sheet' : '加入导出清单' }}
              </el-button>
              <el-button v-if="editingSheetId" plain style="width: 100%; margin-top: 6px" @click="cancelEditSheet">
                取消编辑
              </el-button>
            </div>
          </div>

          <div class="export-sheet-list">
            <div class="panel-header">
              <div>
                <h3>导出清单</h3>
                <p>点击卡片可回填到上方配置区修改，修改后点「更新此 Sheet」。</p>
              </div>
            </div>
            <div v-if="exportSheets.length" class="sheet-card-list">
              <article v-for="(item, itemIdx) in exportSheets" :key="item.id" class="sheet-config-card"
                :class="{ 'is-editing': editingSheetId === item.id }"
                @click="handleSheetCardClick(item)"
              >
                <div class="sheet-config-head">
                  <div>
                    <strong>{{ item.sheetName }}</strong>
                    <p>{{ item.category === 'traffic' ? '流量数据' : '行为数据' }}</p>
                  </div>
                  <div style="display:flex;align-items:center;gap:6px">
                    <el-tag v-if="editingSheetId === item.id" size="small" type="primary">编辑中</el-tag>
                    <div class="sheet-config-actions">
                      <el-button text size="small" :disabled="itemIdx === 0" @click.stop="moveExportSheet(itemIdx, -1)">上移</el-button>
                      <el-button text size="small" :disabled="itemIdx === exportSheets.length - 1" @click.stop="moveExportSheet(itemIdx, 1)">下移</el-button>
                      <el-button text type="danger" size="small" @click.stop="removeExportSheet(item.id)">删除</el-button>
                    </div>
                  </div>
                </div>
                <div class="sheet-config-meta">
                  <span>渠道 {{ item.channels.length }}</span>
                  <span v-if="item.category === 'behavior'">行为 {{ item.behaviors.length }}</span>
                  <el-tag v-if="item.mergedBehaviors && item.mergedBehaviors.length" size="small" type="primary">合并{{ item.mergedBehaviors.length }}组</el-tag>
                </div>
                <p class="sheet-config-text">
                  {{ item.category === 'traffic'
                    ? `渠道 ${item.channelConfigs?.length || 0} 个：${(item.channelConfigs || []).map(c => c.channel === '__overall__' ? '整体' : c.channel).slice(0,3).join('、')}${(item.channelConfigs?.length||0) > 3 ? '等' : ''}`
                    : `渠道：${formatExportTargets(item.channels)}；行为：${formatBehaviorPreview(item.behaviors)}` }}
                </p>
              </article>
            </div>
            <el-empty v-else description="还没有加入任何导出 sheet" />
          </div>

          <div class="export-preview">
            <strong>文件预览</strong>
            <p>{{ exportFileNamePreview }}</p>
            <p>{{ exportSummaryText }}</p>
            <p>日期范围：{{ exportDateText }}</p>
          </div>
        </el-form>

        <div class="drawer-footer">
          <el-button @click="exportDrawerVisible = false">取消</el-button>
          <el-button plain @click="openTemplateSaveDialog">保存配置</el-button>
          <el-button type="primary" :loading="exporting" @click="handleExport">开始导出</el-button>
        </div>
      </div>
    </el-drawer>

    <el-dialog v-model="exportProgressVisible" title="导出进度" width="520px" :close-on-click-modal="false"
      :show-close="false" align-center class="export-progress-dialog">
      <div class="export-progress-panel">
        <div class="export-progress-title" :title="exportProgress.message">{{ exportProgress.message || '准备导出...' }}</div>
        <el-progress :percentage="exportProgress.percentage" :stroke-width="18" :status="exportProgress.status" />
        <div class="export-progress-meta">
          <span>步骤 {{ exportProgress.current }} / {{ exportProgress.total }}</span>
          <span>{{ exportProgress.percentage }}%</span>
          <span>剩余 {{ exportProgress.remaining }} 步</span>
        </div>
        <div class="export-progress-current" :title="exportProgress.currentLabel">
          正在处理：<strong>{{ exportProgress.currentLabel || '-' }}</strong>
        </div>
        <div class="export-progress-log" v-if="exportProgress.logs?.length">
          <div class="export-progress-log-title">处理记录</div>
          <ul class="export-progress-log-list">
            <li
              v-for="(log, idx) in exportProgress.logs"
              :key="log.ts + '-' + idx"
              :class="{ 'is-done': log.done }"
              :title="log.text"
            >{{ log.text }}</li>
          </ul>
        </div>
      </div>
      <template #footer>
        <el-button v-if="exportProgress.status === 'exception'" @click="retryLastExport">
          重试
        </el-button>
        <el-button v-if="exportProgress.status === 'success' || exportProgress.status === 'exception'"
          @click="closeExportProgressDialog">
          关闭
        </el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="trackingCheckVisible" title="埋点数据检测" width="920px" destroy-on-close>
      <div class="tracking-check-shell">
        <div class="tracking-check-head">
          <div>
            <strong>{{ trackingCheckProject?.name || '-' }}</strong>
            <p>{{ trackingCheckProject?.code || '-' }} · {{ normalizeVersion(trackingCheckProject?.version) }} · 检查日期 {{
              trackingCheckDate }}</p>
          </div>
          <div class="tracking-check-actions">
            <el-button :loading="trackingChecking" @click="rerunTrackingCheck">重新检查</el-button>
          </div>
        </div>

        <div class="tracking-progress-panel">
          <div class="tracking-progress-title">{{ trackingCheckProgress.message || '准备检查...' }}</div>
          <el-progress :percentage="trackingCheckProgress.percentage" :stroke-width="18" />
        </div>

        <div class="tracking-summary-grid" v-if="trackingCheckSummary.length">
          <div v-for="item in trackingCheckSummary" :key="item.key" class="tracking-summary-card">
            <div class="tracking-summary-top">
              <strong>{{ item.label }}</strong>
              <el-tag size="small" :type="item.statusType">{{ item.statusText }}</el-tag>
            </div>
            <div class="tracking-summary-values">
              <span>PV {{ formatNumber(item.pv) }}</span>
              <span>UV {{ formatNumber(item.uv) }}</span>
              <span v-if="item.hasValue">Value {{ formatNumber(item.value) }}</span>
            </div>
            <p>{{ item.tip }}</p>
          </div>
        </div>

        <div class="tracking-suggestion-shell" v-if="trackingSuggestions.length">
          <h4>检查建议</h4>
          <ul>
            <li v-for="item in trackingSuggestions" :key="item">{{ item }}</li>
          </ul>
        </div>

        <div class="tracking-other-shell" v-if="trackingOtherRows.length">
          <div class="panel-header">
            <div>
              <h3>其他埋点数据</h3>
              <p>未被当前规则归类的数据，先完整展示，方便你继续扩展规则。</p>
            </div>
          </div>
          <el-table :data="trackingOtherRows" stripe max-height="260">
            <el-table-column prop="type" label="type" min-width="120" />
            <el-table-column prop="customtype" label="customtype" min-width="160" show-overflow-tooltip />
            <el-table-column prop="page" label="page" min-width="140" show-overflow-tooltip />
            <el-table-column prop="module" label="module" min-width="140" show-overflow-tooltip />
            <el-table-column prop="pv" label="PV" min-width="90">
              <template #default="{ row }">{{ formatNumber(row.pv) }}</template>
            </el-table-column>
            <el-table-column prop="uv" label="UV" min-width="90">
              <template #default="{ row }">{{ formatNumber(row.uv) }}</template>
            </el-table-column>
            <el-table-column prop="value" label="Value" min-width="100">
              <template #default="{ row }">{{ formatNumber(row.value) }}</template>
            </el-table-column>
          </el-table>
        </div>

        <div class="tracking-other-shell">
          <div class="panel-header">
            <div>
              <h3>原始聚合结果</h3>
              <p>当前是按 type、customtype、page、module 维度聚合后的今日数据。</p>
            </div>
          </div>
          <el-table :data="trackingCheckRows" stripe max-height="320" v-loading="trackingChecking">
            <el-table-column prop="type" label="type" min-width="120" />
            <el-table-column prop="customtype" label="customtype" min-width="180" show-overflow-tooltip />
            <el-table-column prop="page" label="page" min-width="160" show-overflow-tooltip />
            <el-table-column prop="module" label="module" min-width="160" show-overflow-tooltip />
            <el-table-column prop="pv" label="PV" min-width="90">
              <template #default="{ row }">{{ formatNumber(row.pv) }}</template>
            </el-table-column>
            <el-table-column prop="uv" label="UV" min-width="90">
              <template #default="{ row }">{{ formatNumber(row.uv) }}</template>
            </el-table-column>
            <el-table-column prop="value" label="Value" min-width="100">
              <template #default="{ row }">{{ formatNumber(row.value) }}</template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </el-dialog>

    <ProjectExportDialog
      v-model="projectExportVisible"
      :project="projectExportProject"
      :default-range="dateRange"
      :admin-users="adminUsers"
    />

    <!-- 保存模板弹窗 -->
    <ExportTemplateSaveDialog
      v-model="templateSaveVisible"
      :project-code="exportDrawerProjectInfo.code"
      :project-name="exportDrawerProjectInfo.name"
      :date-range="exportDateRange"
      :sheets="exportSheets"
      :orientation="exportForm.orientation"
      :config-snapshot="buildExportConfigSnapshot()"
      :current-template-id="drawerCurrentTemplateId"
      @saved="handleTemplateSaved"
    />

    <!-- 模板与导出抽屉（项目列表右上角入口） -->
    <ExportRecordDrawer
      v-model="recordDrawerVisible"
      :project-code="null"
      :admin-users="adminUsers"
      :projects="projectOptions"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import * as echarts from 'echarts'
import api from '@/utils/http'
import { buildExportFileName, buildPivotSheet, EXPORT_THEMES, exportWorkbook, getThemeConfig, finalizeBehaviorSheetConfig } from './reportDataExport'
import { createExportProgressController } from './exportProgressUtil'
import { fetchBasicMain, fetchBehaviorDetail, fetchBehaviorList, fetchChannelList, fetchProjectList, resolveProjectIdByCode } from './reportDataApi'
import ProjectExportDialog from './projectExport/ProjectExportDialog.vue'
import { buildStayTimeSql, buildTraceStayTimeSql } from './projectExport/projectExportSqlBuilders'
import ExportTemplateSaveDialog from './components/ExportTemplateSaveDialog.vue'
import ExportRecordDrawer from './components/ExportRecordDrawer.vue'
import { createExportRecord, markRecordRunning, markRecordDone, markRecordFailed, fetchTemplates, fetchTemplateById } from './reportDataTemplateApi'

const activeTab = ref('traffic')
const activeTheme = ref('aurora')
const themeOptions = [
  { label: 'Aurora', value: 'aurora' },
  { label: 'Slate', value: 'slate' },
  { label: 'Graphite', value: 'graphite' }
]
const sortOrderOptions = [
  { label: '降序', value: 'desc' },
  { label: '升序', value: 'asc' }
]
const projectVersionOptions = [
  { label: '全部版本', value: 'all' },
  { label: 'V4', value: 'v4' },
  { label: 'V3', value: 'v3' }
]
const EXPORT_PREFS_KEY = 'report-data-board-export-prefs'
const datePresetOptions = [
  { label: '昨天', value: 'yesterday' },
  { label: '近3天', value: 'last3' },
  { label: '近7天', value: 'last7' },
  { label: '近1个月', value: 'last30' },
  { label: '近3个月', value: 'last90' },
  { label: '今天', value: 'today' }
]
const datePickerShortcuts = datePresetOptions.map((item) => ({
  text: item.label,
  value: () => {
    const [start, end] = getPresetRange(item.value)
    return [new Date(start), new Date(end)]
  }
}))

const dateRange = ref(getDefaultRange())
const compareEnabled = ref(true)
const selectedProjectId = ref(null)
const projectOptions = ref([])
const projectLoading = ref(false)
const projectTotal = ref(0)
const switchingProjectId = ref(null)

const projectQuery = reactive({
  keyword: '',
  page: 1,
  pageSize: 9,
  onlyRunning: false,
  version: 'all',
  sortField: 'id',
  sortOrder: 'desc'
})

const loading = ref(false)
const trafficLoading = ref(false)
const behaviorLoading = ref(false)
const behaviorDetailLoading = ref(false)
const behaviorSearchLoading = ref(false)
const exporting = ref(false)
const exportProgressVisible = ref(false)
const exportProgress = reactive({
  total: 0,
  current: 0,
  percentage: 0,
  message: '',
  status: '',
  currentLabel: '',
  remaining: 0,
  logs: []
})
let exportProgressCtrl = null
let exportAutoCloseTimer = null
const lastExportAction = ref(null)

const currentProject = ref(null)
const viewMode = ref('list')
const channelList = ref([])
/** 导出抽屉独立上下文：与看板 dateRange / channelList 解耦 */
const exportDrawerProject = ref(null)
const exportChannelList = ref([])
const exportDrawerChannelsLoading = ref(false)
const trafficCurrent = ref(createEmptyTraffic())
const trafficCompare = ref(createEmptyTraffic())
const trafficChannelSource = ref(createEmptyTraffic())
const behaviorCurrent = ref([])
const behaviorCompare = ref([])
const behaviorDetail = ref(createEmptyBehaviorDetail())
const behaviorCompareDetail = ref(createEmptyBehaviorDetail())
const behaviorChannelSource = ref(createEmptyBehaviorDetail())

const trafficTrendMetric = ref('pv')
const trafficChannelMetric = ref('uv')
const behaviorTrendMetric = ref('uv')
const behaviorSortMetric = ref('pv')

const behaviorFilters = reactive({
  keyword: ''
})
const selectedChannel = ref('')

const selectedBehaviorName = ref('')
const selectedBehaviorSummary = ref(null)
const behaviorSelection = ref([])

const exportDrawerVisible = ref(false)
const exportThemes = EXPORT_THEMES
const ALL_TRAFFIC_METRICS = [
  { key: 'pv', label: 'PV' },
  { key: 'uv', label: 'UV' },
  { key: 'clickUser', label: '点击人数' },
  { key: 'clickCount', label: '点击次数' },
  { key: 'sumStayTime', label: '总停留时长' },
  { key: 'avgStayTime', label: '平均停留时长' },
  { key: 'clickRate', label: '点击率' },
  { key: 'dropRate', label: '流失率' }
]
const ALL_METRIC_KEYS = ALL_TRAFFIC_METRICS.map((m) => m.key)
// 整体默认：PV UV 平均停留时长
const DEFAULT_OVERALL_METRICS = ['pv', 'uv', 'avgStayTime']
// 其他渠道默认：PV UV
const DEFAULT_CHANNEL_METRICS = ['pv', 'uv']
const exportForm = reactive({
  orientation: 'date-row',
  metricMode: 'both',
  themeKey: exportThemes[0].key,
  selectedMetrics: ['pv', 'uv'],
  includeSummaryRow: false,
  summaryDedup: false,
  summaryCumDaily: false,
  stayTimeSource: 'dataworks',
  stayAvgBase: 'visitUv'
})
const exportDateRange = ref(getDefaultRange())
const exportSheetDraft = reactive({
  category: 'traffic',
  sheetName: '',
  // 流量Sheet：渠道+各自指标（每个渠道独立控制）
  channelConfigs: [{ channel: '__overall__', metrics: [...['pv', 'uv', 'avgStayTime']] }],
  // 行为Sheet：纯渠道列表
  channels: ['__overall__'],
  behaviorKeyword: '',
  behaviors: [],         // 独立行为（直接加入 Sheet）
  sortBehaviors: true,   // 添加独立行为/合并组成员时是否按字母序排序（含导出行顺序）
  mergedBehaviors: [],   // 合并组：{ name, behaviors[], keepOriginal }
  searchSelected: [],    // 搜索结果里当前勾选的行为（临时暂存）
  mergeName: '',         // 合并组名称输入
  editingGroupIdx: null  // 正在编辑的合并组索引（null=新建模式）
})
const exportSheets = ref([])
/** 当前 draft 回填后的快照，用于判断切换 Sheet 时是否真有改动 */
const exportDraftBaseline = ref('')
const exportBehaviorSearchLoading = ref(false)
const exportBehaviorPool = ref([])
const exportExcludeOtherSheetBehaviors = ref(true)
const exportExcludeCurrentSheetBehaviors = ref(true)
const exportExcludeMergeGroupBehaviors = ref(true)
const mergeGroupEditBaseline = ref('')
const stayDetecting = ref(false)
const stayDetectResult = ref(null)
const trackingCheckVisible = ref(false)
const projectExportVisible = ref(false)
const projectExportProject = ref(null)

// 模板保存 & 导出记录
const templateSaveVisible  = ref(false)
const recordDrawerVisible  = ref(false)
const currentExportRecordId = ref(null)
// 管理员列表（从字典读取，key: export_admin_user_list）
const adminUsers = ref([])
// 导出抽屉内的模板快速载入
const drawerTemplates = ref([])
const drawerTemplatesLoading = ref(false)
const drawerSelectedTemplateId = ref(null)
// 当前已载入/保存的模板 ID（有值=覆盖更新，null=新增）
const drawerCurrentTemplateId = ref(null)
// 当前正在编辑的 Sheet ID（点击卡片回填时高亮）
const editingSheetId = ref(null)
const trackingCheckProject = ref(null)
const trackingCheckRows = ref([])
const trackingCheckSummary = ref([])
const trackingOtherRows = ref([])
const trackingSuggestions = ref([])
const trackingChecking = ref(false)
const trackingCheckLoadingId = ref(null)
const trackingCheckDate = ref(formatDate(new Date()))
const trackingCheckProgress = reactive({
  percentage: 0,
  message: ''
})

const trafficTrendChartRef = ref(null)
const trafficChannelChartRef = ref(null)
const behaviorTrendChartRef = ref(null)
const behaviorChannelChartRef = ref(null)

let trafficTrendChart = null
let trafficChannelChart = null
let behaviorTrendChart = null
let behaviorChannelChart = null
let dashboardRequestSeq = 0
let behaviorSearchTimer = null
let exportBehaviorSearchTimer = null
const TRACKING_CHECK_RULES = [
  {
    key: 'traffic',
    label: '流量',
    hasValue: false,
    match(version, row) {
      return version === 'v3' ? row.type === 'flowSource' : row.type === 'visit'
    }
  },
  {
    key: 'click',
    label: '点击',
    hasValue: false,
    match(version, row) {
      return version === 'v3' ? Boolean(String(row.customtype || '').trim()) : row.type === 'click'
    }
  },
  {
    key: 'stayTime',
    label: '停留时长',
    hasValue: true,
    match(_version, row) {
      return row.type === 'timeOnSite' && toNumber(row.value) > 0
    }
  }
]

const trafficMetricOptions = [
  { label: 'PV', value: 'pv' },
  { label: 'UV', value: 'uv' },
  { label: '点击人数', value: 'clickUser' },
  { label: '点击次数', value: 'clickCount' }
]

const activeDateText = computed(() => `${dateRange.value?.[0] || '-'} ~ ${dateRange.value?.[1] || '-'}`)
const exportDateText = computed(() => `${exportDateRange.value?.[0] || '-'} ~ ${exportDateRange.value?.[1] || '-'}`)
const compareDateText = computed(() => {
  const range = getCompareRange(dateRange.value)
  return `${range[0]} ~ ${range[1]}`
})

const channelOptions = computed(() => {
  return (channelList.value || []).map((item) => ({
    label: item,
    value: item
  }))
})

const exportDrawerChannelOptions = computed(() => {
  return (exportChannelList.value || []).map((item) => ({
    label: item,
    value: item
  }))
})

const exportDrawerProjectInfo = computed(() => {
  const project = exportDrawerProject.value || currentProject.value
  return {
    code: project?.code || '',
    name: project?.name || ''
  }
})

const displayProjectOptions = computed(() => {
  let list = !projectQuery.onlyRunning
    ? [...projectOptions.value]
    : projectOptions.value.filter((item) => item.running)

  if (projectQuery.version !== 'all') {
    list = list.filter((item) => normalizeVersion(item.version).toLowerCase() === projectQuery.version)
  }

  if (!selectedProjectId.value) return list
  return list.sort((a, b) => {
    if (a.id === selectedProjectId.value) return -1
    if (b.id === selectedProjectId.value) return 1
    return 0
  })
})

const trafficKpis = computed(() => {
  const current = trafficCurrent.value || createEmptyTraffic()
  const compare = trafficCompare.value || createEmptyTraffic()
  return [
    buildKpi('pv', '访问 PV', current.pv, compare.pv, '整体访问量', 'primary'),
    buildKpi('uv', '访问 UV', current.uv, compare.uv, '去重访客量', 'success'),
    buildKpi('clickUser', '点击人数', current.clickUser, compare.clickUser, '有行为的用户数', 'warning'),
    buildKpi('clickCount', '点击次数', current.clickCount, compare.clickCount, '总交互次数', 'danger'),
    buildKpi('clickRate', '点击率', Number(current.clickRate || 0), Number(compare.clickRate || 0), '点击人数 / UV', 'info', true),
    buildKpi('dropRate', '流失率', Number(current.dropRate || 0), Number(compare.dropRate || 0), '1 - 点击率', 'info', true)
  ]
})

const trafficChannelRows = computed(() => {
  return [...(trafficChannelSource.value?.channelRateList || [])].sort((a, b) => (b[trafficChannelMetric.value] || 0) - (a[trafficChannelMetric.value] || 0))
})

const trafficDailyRows = computed(() => {
  const rows = trafficCurrent.value?.dailyBasics || []
  return rows.map((item, index) => {
    const prev = index > 0 ? rows[index - 1] : null
    return {
      ...item,
      pvMom: calcMom(item.pv, prev?.pv),
      uvMom: calcMom(item.uv, prev?.uv),
      clickUserMom: calcMom(item.clickUser, prev?.clickUser),
      clickCountMom: calcMom(item.clickCount, prev?.clickCount)
    }
  })
})

const behaviorRows = computed(() => {
  const compareMap = new Map((behaviorCompare.value || []).map((item) => [item.indexName, item]))
  const totalPv = (behaviorCurrent.value || []).reduce((sum, item) => sum + toNumber(item.pv), 0)
  const totalUv = (behaviorCurrent.value || []).reduce((sum, item) => sum + toNumber(item.uv), 0)

  return (behaviorCurrent.value || []).map((item) => {
    const compare = compareMap.get(item.indexName) || {}
    const pv = toNumber(item.pv)
    const uv = toNumber(item.uv)
    const comparePv = toNumber(compare.pv)
    const compareUv = toNumber(compare.uv)
    return {
      ...item,
      pvShare: calcRate(pv, totalPv),
      uvShare: calcRate(uv, totalUv),
      pvDelta: pv - comparePv,
      uvDelta: uv - compareUv,
      pvDeltaRate: calcMom(pv, comparePv),
      uvDeltaRate: calcMom(uv, compareUv)
    }
  }).sort((a, b) => b.pv - a.pv)
})

const behaviorTopRows = computed(() => {
  const key = behaviorSortMetric.value
  return [...behaviorRows.value]
    .sort((a, b) => toNumber(b[key]) - toNumber(a[key]))
    .slice(0, 10)
})

const behaviorSelectOptions = computed(() => {
  return behaviorRows.value.map((item) => ({
    label: item.indexName,
    value: item.indexName
  }))
})

const exportBehaviorOptions = computed(() => {
  const standaloneInOtherSheets = new Set(
    exportSheets.value
      .filter((item) => item.category === 'behavior')
      .flatMap((item) => item.behaviors || [])
  )

  const standaloneInCurrentSheet = new Set(exportSheetDraft.behaviors)

  const findMergeInfo = (mergedBehaviors, behaviorName, editingIdx = exportSheetDraft.editingGroupIdx) => {
    for (let i = 0; i < (mergedBehaviors || []).length; i++) {
      const m = mergedBehaviors[i]
      const members = getMergedGroupBehaviors(i, editingIdx)
      if (members.includes(behaviorName)) {
        return {
          name: m.name || members.join('+'),
          keepOriginal: !!m.keepOriginal
        }
      }
    }
    return null
  }

  const findAnyMergeInfo = (behaviorName) => {
    const current = findMergeInfo(exportSheetDraft.mergedBehaviors, behaviorName)
    if (current) return { scope: 'current', ...current }
    for (const sheet of exportSheets.value.filter((s) => s.category === 'behavior')) {
      const other = findMergeInfo(sheet.mergedBehaviors, behaviorName, null)
      if (other) return { scope: 'other', ...other }
    }
    return null
  }

  return exportBehaviorPool.value
    .filter((item) => {
      const name = item.indexName
      if (exportExcludeCurrentSheetBehaviors.value && standaloneInCurrentSheet.has(name)) return false
      if (exportExcludeOtherSheetBehaviors.value && standaloneInOtherSheets.has(name)) return false

      const mergeInfo = findAnyMergeInfo(name)
      if (mergeInfo?.keepOriginal) return false
      if (exportExcludeMergeGroupBehaviors.value && mergeInfo) return false
      return true
    })
    .map((item) => {
      const name = item.indexName
      const mergeInfo = findAnyMergeInfo(name)
      return {
        label: item.indexName,
        value: item.indexName,
        pv: item.pv,
        uv: item.uv,
        mergeGroupLabel: mergeInfo && !mergeInfo.keepOriginal ? mergeInfo.name : ''
      }
    })
})

const exportFileNamePreview = computed(() => {
  const project = exportDrawerProject.value || currentProject.value
  return buildExportFileName({
    projectName: project?.name,
    projectCode: project?.code,
    dataLabel: exportSheets.value.length > 1 ? '组合导出' : exportSheets.value[0]?.category === 'behavior' ? '行为导出' : '流量导出',
    startDate: exportDateRange.value?.[0] || 'start',
    endDate: exportDateRange.value?.[1] || 'end'
  })
})

const exportSummaryText = computed(() => {
  const trafficCount = exportSheets.value.filter((item) => item.category === 'traffic').length
  const behaviorCount = exportSheets.value.filter((item) => item.category === 'behavior').length
  const mergedCount = exportSheets.value.reduce((sum, item) => sum + (item.mergedBehaviors?.length || 0), 0)
  const parts = [`已配置 ${exportSheets.value.length} 个 sheet（流量 ${trafficCount}、行为 ${behaviorCount}${mergedCount ? '、合并' + mergedCount + '组' : ''}）`]
  if (exportForm.includeSummaryRow) {
    const modes = []
    if (exportForm.summaryDedup) modes.push('去重')
    if (exportForm.summaryCumDaily) modes.push('累加')
    parts.push(`含汇总行（${modes.length ? modes.join('+') : '求和'}）`)
  }
  return parts.join('；')
})

const metricModeLabel = computed(() => {
  const labels = (exportForm.selectedMetrics || []).map((k) => {
    const m = { pv: 'PV', uv: 'UV', clickUser: '点击人数', clickCount: '点击次数', sumStayTime: '总停留', avgStayTime: '均停留', clickRate: '点击率', dropRate: '流失率' }
    return m[k] || k
  })
  return labels.length ? labels.join('+') : '未选指标'
})

const exportThemeLabel = computed(() => {
  return getThemeConfig(exportForm.themeKey).label
})

const dateDiffDays = computed(() => {
  if (!exportDateRange.value?.[0] || !exportDateRange.value?.[1]) return 0
  const start = new Date(exportDateRange.value[0])
  const end = new Date(exportDateRange.value[1])
  return Math.round((end.getTime() - start.getTime()) / 86400000) + 1
})

watch(activeTab, async (value) => {
  if (value === 'behavior' && currentProject.value) {
    await loadBehaviorData()
  }
  await nextTick()
  renderCharts()
  // lazy tab 切换后图表容器首次挂载，需要多等一帧再 resize
  await nextTick()
  resizeCharts()
})

watch([dateRange, compareEnabled], async () => {
  if (!currentProject.value) return
  await refreshAll()
})

watch(exportDateRange, async () => {
  if (!exportDrawerVisible.value) {
    exportBehaviorPool.value = []
    return
  }
  await refreshExportDrawerData()
})

watch(activeTheme, async () => {
  await nextTick()
  renderCharts()
})

watch(
  () => ({
    orientation: exportForm.orientation,
    metricMode: exportForm.metricMode,
    themeKey: exportForm.themeKey,
    selectedMetrics: exportForm.selectedMetrics,
    includeSummaryRow: exportForm.includeSummaryRow,
    summaryDedup: exportForm.summaryDedup,
    summaryCumDaily: exportForm.summaryCumDaily,
    stayTimeSource: exportForm.stayTimeSource,
    stayAvgBase: exportForm.stayAvgBase
  }),
  (value) => {
    localStorage.setItem(EXPORT_PREFS_KEY, JSON.stringify(value))
  },
  { deep: true }
)

watch(
  () => [trafficTrendMetric.value, trafficChannelMetric.value, behaviorTrendMetric.value],
  async () => {
    await nextTick()
    renderCharts()
  }
)

watch(
  () => [trafficCurrent.value, trafficChannelSource.value, behaviorDetail.value, behaviorChannelSource.value, selectedBehaviorName.value],
  async () => {
    await nextTick()
    renderCharts()
  },
  { deep: true }
)

onMounted(async () => {
  restoreExportPreferences()
  await searchProjects()
  window.addEventListener('resize', resizeCharts)
  // 加载管理员列表（字典 key: export_admin_user_list，值为 username 数组）
  try {
    const dictVal = await api.getDictByCode('export_admin_user_list')
    if (Array.isArray(dictVal)) {
      adminUsers.value = dictVal
    } else if (typeof dictVal === 'string') {
      adminUsers.value = dictVal.split(',').map(s => s.trim()).filter(Boolean)
    }
  } catch {
    // 字典不存在时忽略，降级为空数组
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeCharts)
  clearBehaviorSearchTimer()
  clearExportBehaviorSearchTimer()
  clearExportAutoCloseTimer()
  disposeCharts()
})

async function loadProjectOptions(query) {
  projectLoading.value = true
  try {
    if (query !== projectQuery.keyword) {
      projectQuery.keyword = query || ''
      projectQuery.page = 1
    }
    const result = await fetchProjectList({
      name: query ?? projectQuery.keyword,
      page: projectQuery.page,
      size: projectQuery.pageSize,
      sortField: projectQuery.sortField,
      sortOrder: projectQuery.sortOrder
    })
    projectOptions.value = result?.list || []
    projectTotal.value = result?.total || 0
    if (selectedProjectId.value) {
      const matched = projectOptions.value.find((item) => item.id === selectedProjectId.value)
      if (matched) currentProject.value = matched
    }
  } catch (error) {
    console.error(error)
  } finally {
    projectLoading.value = false
  }
}

async function searchProjects() {
  await loadProjectOptions(projectQuery.keyword)
}

async function resetProjectQuery() {
  projectQuery.keyword = ''
  projectQuery.page = 1
  projectQuery.pageSize = 9
  projectQuery.onlyRunning = false
  projectQuery.version = 'all'
  projectQuery.sortField = 'id'
  projectQuery.sortOrder = 'desc'
  await searchProjects()
}

async function handleProjectRunningFilterChange() {
  if (selectedProjectId.value && !displayProjectOptions.value.find((item) => item.id === selectedProjectId.value)) {
    // keep current project in dashboard, only list filtered
  }
}

async function selectProject(project) {
  if (!project?.id) return
  if (switchingProjectId.value === project.id || loading.value) return
  switchingProjectId.value = project.id
  selectedProjectId.value = project.id
  currentProject.value = project
  selectedChannel.value = ''
  behaviorFilters.keyword = ''
  resetBehaviorDetail()
  viewMode.value = 'board'
  activeTab.value = 'traffic'
  try {
    await refreshAll()
    await nextTick()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } finally {
    if (switchingProjectId.value === project.id) {
      switchingProjectId.value = null
    }
  }
}

function restoreExportPreferences() {
  try {
    const raw = localStorage.getItem(EXPORT_PREFS_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw)
    if (parsed?.orientation) exportForm.orientation = parsed.orientation
    if (parsed?.metricMode) exportForm.metricMode = parsed.metricMode
    if (parsed?.selectedMetrics) exportForm.selectedMetrics = parsed.selectedMetrics
    if (parsed?.includeSummaryRow !== undefined) exportForm.includeSummaryRow = parsed.includeSummaryRow
    if (parsed?.summaryDedup !== undefined) exportForm.summaryDedup = parsed.summaryDedup
    if (parsed?.summaryCumDaily !== undefined) exportForm.summaryCumDaily = parsed.summaryCumDaily
    if (parsed?.stayTimeSource) exportForm.stayTimeSource = parsed.stayTimeSource
    if (parsed?.stayAvgBase) exportForm.stayAvgBase = parsed.stayAvgBase
    if (parsed?.themeKey) exportForm.themeKey = parsed.themeKey
  } catch (error) {
    console.error('restore export prefs failed', error)
  }
}

async function refreshAll() {
  if (!currentProject.value?.id) return
  const requestId = ++dashboardRequestSeq
  const projectId = currentProject.value.id
  loading.value = true
  try {
    await Promise.all([
      loadTrafficData({ requestId, projectId }),
      activeTab.value === 'behavior' ? loadBehaviorData({ requestId, projectId }) : Promise.resolve()
    ])
    if (requestId !== dashboardRequestSeq || currentProject.value?.id !== projectId) return
  } finally {
    if (requestId === dashboardRequestSeq) {
      loading.value = false
    }
  }
}

async function loadTrafficData(context = {}) {
  trafficLoading.value = true
  try {
    const currentParams = buildBaseParams()
    const compareParams = buildBaseParams(getCompareRange(dateRange.value))
    const allChannelParams = buildBaseParams(dateRange.value, false)

    const tasks = [
      fetchBasicMain(currentParams),
      fetchBasicMain(allChannelParams),
      fetchChannelList(allChannelParams)
    ]
    if (compareEnabled.value) {
      tasks.push(fetchBasicMain(compareParams))
    }

    const [main, allChannelMain, channels, compareMain] = await Promise.all(tasks)
    if (context.requestId && (context.requestId !== dashboardRequestSeq || currentProject.value?.id !== context.projectId)) {
      return
    }
    trafficCurrent.value = normalizeTraffic(main, dateRange.value)
    trafficChannelSource.value = normalizeTraffic(allChannelMain, dateRange.value)
    channelList.value = Array.isArray(channels) ? channels : []
    trafficCompare.value = compareEnabled.value ? normalizeTraffic(compareMain, getCompareRange(dateRange.value)) : createEmptyTraffic()

    // 数据更新完毕后主动刷新图表（避免 lazy tab 初始化时机问题）
    if (activeTab.value === 'traffic') {
      await nextTick()
      renderTrafficTrendChart()
      renderTrafficChannelChart()
    }
  } catch (error) {
    console.error(error)
    ElMessage.error(error.message || '加载流量数据失败')
  } finally {
    if (!context.requestId || context.requestId === dashboardRequestSeq) {
      trafficLoading.value = false
    }
  }
}

async function loadBehaviorData(context = {}) {
  if (!currentProject.value?.id) return
  behaviorLoading.value = true
  try {
    const currentParams = {
      ...buildBaseParams(),
      channel: selectedChannel.value || '',
      indexName: behaviorFilters.keyword || ''
    }
    const compareParams = {
      ...buildBaseParams(getCompareRange(dateRange.value)),
      channel: selectedChannel.value || '',
      indexName: behaviorFilters.keyword || ''
    }

    const tasks = [fetchBehaviorList(currentParams)]
    if (compareEnabled.value) {
      tasks.push(fetchBehaviorList(compareParams))
    }
    const [list, compareList] = await Promise.all(tasks)
    if (context.requestId && (context.requestId !== dashboardRequestSeq || currentProject.value?.id !== context.projectId)) {
      return
    }
    behaviorCurrent.value = normalizeBehaviorList(list)
    behaviorCompare.value = compareEnabled.value ? normalizeBehaviorList(compareList) : []

    if (!selectedBehaviorName.value && behaviorCurrent.value.length > 0) {
      await showBehaviorDetail(behaviorCurrent.value[0])
    } else if (selectedBehaviorName.value) {
      const found = behaviorCurrent.value.find((item) => item.indexName === selectedBehaviorName.value)
      if (found) {
        await showBehaviorDetail(found)
      } else if (behaviorCurrent.value.length > 0) {
        await showBehaviorDetail(behaviorCurrent.value[0])
      } else {
        resetBehaviorDetail()
      }
    }
  } catch (error) {
    console.error(error)
    ElMessage.error(error.message || '加载行为数据失败')
  } finally {
    if (!context.requestId || context.requestId === dashboardRequestSeq) {
      behaviorLoading.value = false
    }
  }
}

async function refreshBehavior() {
  await loadBehaviorData()
}

function handleBehaviorKeywordSearch(query) {
  clearBehaviorSearchTimer()
  behaviorSearchLoading.value = true
  behaviorSearchTimer = window.setTimeout(async () => {
    behaviorFilters.keyword = query || ''
    try {
      await loadBehaviorData()
    } finally {
      behaviorSearchLoading.value = false
    }
  }, 280)
}

async function handleBehaviorSelectVisibleChange(visible) {
  if (!visible || behaviorRows.value.length) return
  behaviorSearchLoading.value = true
  try {
    behaviorFilters.keyword = ''
    await loadBehaviorData()
  } finally {
    behaviorSearchLoading.value = false
  }
}

async function handleBehaviorSelectChange(value) {
  if (!value) {
    if (behaviorCurrent.value.length > 0) {
      await showBehaviorDetail(behaviorCurrent.value[0])
    } else {
      resetBehaviorDetail()
    }
    return
  }
  const found = behaviorRows.value.find((item) => item.indexName === value)
  if (found) {
    await showBehaviorDetail(found)
    return
  }
  behaviorFilters.keyword = value
  behaviorSearchLoading.value = true
  try {
    await loadBehaviorData()
    const matched = behaviorRows.value.find((item) => item.indexName === value)
    if (matched) {
      await showBehaviorDetail(matched)
    }
  } finally {
    behaviorSearchLoading.value = false
  }
}

async function handleBehaviorKeywordClear() {
  behaviorFilters.keyword = ''
  behaviorSearchLoading.value = true
  try {
    await loadBehaviorData()
  } finally {
    behaviorSearchLoading.value = false
  }
}

async function showBehaviorDetail(row) {
  if (!row?.indexName) return
  selectedBehaviorName.value = row.indexName
  selectedBehaviorSummary.value = behaviorRows.value.find((item) => item.indexName === row.indexName) || null

  try {
    behaviorDetailLoading.value = true
    const currentParams = {
      ...buildBaseParams(dateRange.value, false),
      indexName: row.indexName,
      channel: selectedChannel.value || ''
    }
    const compareParams = {
      ...buildBaseParams(getCompareRange(dateRange.value), false),
      indexName: row.indexName,
      channel: selectedChannel.value || ''
    }
    const channelDetailParams = {
      ...buildBaseParams(dateRange.value, false),
      indexName: row.indexName,
      channel: ''
    }
    const tasks = [fetchBehaviorDetail(currentParams), fetchBehaviorDetail(channelDetailParams)]
    if (compareEnabled.value) tasks.push(fetchBehaviorDetail(compareParams))
    const [detail, channelDetail, compareDetail] = await Promise.all(tasks)
    behaviorDetail.value = normalizeBehaviorDetail(detail, dateRange.value)
    behaviorChannelSource.value = normalizeBehaviorDetail(channelDetail, dateRange.value)
    behaviorCompareDetail.value = compareEnabled.value ? normalizeBehaviorDetail(compareDetail, getCompareRange(dateRange.value)) : createEmptyBehaviorDetail()
  } catch (error) {
    console.error(error)
    ElMessage.error(error.message || '加载行为明细失败')
  } finally {
    behaviorDetailLoading.value = false
  }
}

function handleBehaviorRowClick(row) {
  showBehaviorDetail(row)
}

function handleBehaviorSelectionChange(rows) {
  behaviorSelection.value = rows.map((item) => item.indexName)
}

function topBehaviorRowClassName({ row }) {
  return row?.indexName && row.indexName === selectedBehaviorName.value ? 'is-active-top-row' : ''
}

function resetBehaviorDetail() {
  selectedBehaviorName.value = ''
  selectedBehaviorSummary.value = null
  behaviorDetail.value = createEmptyBehaviorDetail()
  behaviorCompareDetail.value = createEmptyBehaviorDetail()
  behaviorChannelSource.value = createEmptyBehaviorDetail()
}

function buildBaseParams(range = dateRange.value, includeChannel = true) {
  const params = {
    projectId: currentProject.value?.id,
    startTime: range?.[0],
    endTime: range?.[1]
  }
  if (includeChannel && selectedChannel.value) {
    params.channel = selectedChannel.value
  }
  return params
}

function getExportDrawerProject() {
  return exportDrawerProject.value || currentProject.value
}

function buildExportBaseParams(range = exportDateRange.value) {
  const project = getExportDrawerProject()
  return {
    projectId: project?.id,
    startTime: range?.[0],
    endTime: range?.[1]
  }
}

function getExportDrawerChannelValues() {
  return exportDrawerChannelOptions.value.map((c) => c.value)
}

async function loadExportDrawerChannels() {
  const project = getExportDrawerProject()
  if (!project?.id || !exportDateRange.value?.[0]) {
    exportChannelList.value = []
    return
  }
  exportDrawerChannelsLoading.value = true
  try {
    const channels = await fetchChannelList(buildExportBaseParams(exportDateRange.value))
    exportChannelList.value = Array.isArray(channels) ? channels : []
  } catch (error) {
    console.error(error)
    exportChannelList.value = []
    ElMessage.error(error.message || '加载导出渠道失败')
  } finally {
    exportDrawerChannelsLoading.value = false
  }
}

/** 打开抽屉或切换导出日期时：按抽屉日期重新拉渠道与行为池 */
async function refreshExportDrawerData() {
  await loadExportDrawerChannels()
  exportBehaviorPool.value = []
  exportSheetDraft.searchSelected = []
  if (exportSheetDraft.category === 'behavior') {
    exportBehaviorSearchLoading.value = true
    try {
      await loadExportBehaviorOptions(exportSheetDraft.behaviorKeyword || '')
    } finally {
      exportBehaviorSearchLoading.value = false
    }
  }
}

async function openExportDrawer() {
  if (!currentProject.value) {
    ElMessage.warning('请先选择项目')
    return
  }
  exportDrawerProject.value = currentProject.value
  exportDateRange.value = Array.isArray(dateRange.value) ? [...dateRange.value] : getDefaultRange()
  stayDetectResult.value = null
  drawerSelectedTemplateId.value = null
  drawerCurrentTemplateId.value = null
  if (!exportSheets.value.length) {
    resetExportSheetDraft(activeTab.value === 'behavior' ? 'behavior' : 'traffic', [])
  }
  exportDrawerVisible.value = true
  loadDrawerTemplates()
  await refreshExportDrawerData()
  if (!exportSheets.value.length) {
    resetExportSheetDraft(
      activeTab.value === 'behavior' ? 'behavior' : 'traffic',
      getExportDrawerChannelValues()
    )
  } else {
    await syncDraftAfterSheetsLoaded()
  }
}

async function loadDrawerTemplates() {
  const projectCode = getExportDrawerProject()?.code
  if (!projectCode) return
  drawerTemplatesLoading.value = true
  try {
    const rows = await fetchTemplates(projectCode, { adminUsers: adminUsers.value })
    drawerTemplates.value = (Array.isArray(rows) ? rows : []).map(tpl => {
      try {
        tpl._config = typeof tpl.config_json === 'string' ? JSON.parse(tpl.config_json) : tpl.config_json
      } catch { tpl._config = null }
      return tpl
    })
  } catch (err) {
    console.warn('加载模板列表失败:', err.message)
  } finally {
    drawerTemplatesLoading.value = false
  }
}

async function handleDrawerLoadTemplate(templateId) {
  if (!templateId) return
  drawerTemplatesLoading.value = true
  try {
    await loadExportDrawerChannels()
    // 列表不含 config_json，需要查详情
    const detail = await fetchTemplateById(templateId)
    if (!detail) { ElMessage.warning('模板不存在'); return }
    const config = typeof detail.config_json === 'string'
      ? JSON.parse(detail.config_json) : detail.config_json
    if (!config) { ElMessage.warning('模板配置数据为空'); return }

    // 还原 exportForm 配置
    if (config.exportForm) {
      const safeKeys = ['orientation', 'themeKey', 'includeSummaryRow', 'summaryDedup', 'summaryCumDaily', 'stayTimeSource', 'stayAvgBase', 'selectedMetrics']
      safeKeys.forEach(k => { if (config.exportForm[k] !== undefined) exportForm[k] = config.exportForm[k] })
    }
    // 还原 Sheet 列表，同时合并当前实际渠道信息
    if (Array.isArray(config.exportSheets) && config.exportSheets.length) {
      const currentChannelSet = new Set(getExportDrawerChannelValues())
      exportSheets.value = config.exportSheets.map(s => {
        const newSheet = { ...s, id: `sheet_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` }
        if (s.category === 'traffic' && s.channelConfigs) {
          // 标记模板里有但当前项目没有的渠道
          newSheet.channelConfigs = s.channelConfigs.map(cfg => ({
            ...cfg,
            metrics: [...cfg.metrics],
            _missing: cfg.channel !== '__overall__' && !currentChannelSet.has(cfg.channel)
          }))
        }
        return newSheet
      })
    }
    // 选中该模板（同步 select）
    drawerSelectedTemplateId.value = templateId
    drawerCurrentTemplateId.value  = templateId
    ElMessage.success(`已载入模板，${config.exportSheets?.length || 0} 个 Sheet`)
    await syncDraftAfterSheetsLoaded()
  } catch (err) {
    ElMessage.error('载入模板失败：' + err.message)
  } finally {
    drawerTemplatesLoading.value = false
  }
}

function serializeExportDraftState() {
  if (exportSheetDraft.category === 'traffic') {
    return JSON.stringify({
      sheetName: exportSheetDraft.sheetName,
      channelConfigs: exportSheetDraft.channelConfigs.map((c) => ({
        channel: c.channel,
        metrics: [...(c.metrics || [])].sort()
      }))
    })
  }
  return JSON.stringify({
    sheetName: exportSheetDraft.sheetName,
    channels: [...(exportSheetDraft.channels || [])].sort(),
    sortBehaviors: !!exportSheetDraft.sortBehaviors,
    behaviors: [...(exportSheetDraft.behaviors || [])],
    mergedBehaviors: (exportSheetDraft.mergedBehaviors || []).map((m) => ({
      name: m.name,
      behaviors: [...(m.behaviors || [])],
      keepOriginal: !!m.keepOriginal
    }))
  })
}

function captureExportDraftBaseline() {
  exportDraftBaseline.value = serializeExportDraftState()
}

/** 与回填快照对比，仅在有实际修改时视为脏数据 */
function isDraftDirty() {
  if (!exportDraftBaseline.value) return false
  return serializeExportDraftState() !== exportDraftBaseline.value
}

/** 载入模板/记录后：默认回填第一个 Sheet，避免空 draft 被误判为脏数据 */
async function syncDraftAfterSheetsLoaded() {
  editingSheetId.value = null
  if (exportSheets.value.length) {
    await loadSheetToDraft(exportSheets.value[0], { silent: true })
  } else {
    resetExportSheetDraft('traffic', getExportDrawerChannelValues())
    captureExportDraftBaseline()
  }
}

/** 点击 Sheet 卡片：检查 draft 脏数据，提示后回填 */
async function handleSheetCardClick(sheetItem) {
  if (isDraftDirty()) {
    try {
      await ElMessageBox.confirm(
        '当前配置区有未加入清单的内容，切换后会丢失，是否继续？',
        '提示',
        {
          confirmButtonText: '加入清单并切换',
          cancelButtonText: '直接切换（丢弃）',
          distinguishCancelAndClose: true,
          type: 'warning'
        }
      )
      // 点"加入清单并切换"
      appendExportSheet()
    } catch (action) {
      if (action === 'cancel') {
        // 点"直接切换（丢弃）"—— 继续执行回填
      } else {
        // 点×关闭弹窗——取消操作
        return
      }
    }
  }
  loadSheetToDraft(sheetItem)
}

/** 把已有 Sheet 配置回填到 draft 工作区 */
async function loadSheetToDraft(sheetItem, options = {}) {
  editingSheetId.value = sheetItem.id
  const allChannelValues = getExportDrawerChannelValues()

  // 重置 draft，然后回填
  exportSheetDraft.category    = sheetItem.category
  exportSheetDraft.sheetName   = sheetItem.sheetName

  if (sheetItem.category === 'traffic') {
    exportSheetDraft.channelConfigs = sheetItem.channelConfigs?.length
      ? sheetItem.channelConfigs.map(c => ({ ...c, metrics: [...c.metrics] }))
      : allChannelValues.map(ch => ({ channel: ch, metrics: ch === '__overall__' ? [...DEFAULT_OVERALL_METRICS] : [...DEFAULT_CHANNEL_METRICS] }))
    exportSheetDraft.channels = ['__overall__']
  } else {
    // 行为
    exportSheetDraft.channels       = sheetItem.channels?.length ? [...sheetItem.channels] : ['__overall__']
    exportSheetDraft.behaviors      = [...(sheetItem.behaviors || [])]
    exportSheetDraft.sortBehaviors  = sheetItem.sortBehaviors === true
    exportSheetDraft.mergedBehaviors= (sheetItem.mergedBehaviors || []).map(m => ({ ...m, behaviors: [...m.behaviors] }))
    exportSheetDraft.searchSelected = []
    exportSheetDraft.mergeName      = ''
    exportSheetDraft.editingGroupIdx = null
    exportBehaviorPool.value = []
    // 加载行为数据池
    if (exportSheetDraft.behaviors.length || exportSheetDraft.mergedBehaviors.length) {
      exportBehaviorSearchLoading.value = true
      try {
        await loadExportBehaviorOptions('')
      } finally {
        exportBehaviorSearchLoading.value = false
      }
    }
  }

  if (!options.silent) {
    ElMessage.info(`已回填「${sheetItem.sheetName}」，修改后点「更新此 Sheet」`)
  }
  captureExportDraftBaseline()
}

async function handleExportDraftCategoryChange(category) {
  const allChannelValues = getExportDrawerChannelValues()
  resetExportSheetDraft(category, allChannelValues)
  if (category === 'behavior') {
    exportBehaviorSearchLoading.value = true
    try {
      await loadExportBehaviorOptions('')
    } finally {
      exportBehaviorSearchLoading.value = false
    }
  }
}

function selectDraftChannels() {
  if (exportSheetDraft.category === 'traffic') {
    const allChannels = ['__overall__', ...getExportDrawerChannelValues()]
    allChannels.forEach((channel) => {
      if (!exportSheetDraft.channelConfigs.find((c) => c.channel === channel)) {
        exportSheetDraft.channelConfigs.push({
          channel,
          metrics: channel === '__overall__' ? [...DEFAULT_OVERALL_METRICS] : [...DEFAULT_CHANNEL_METRICS]
        })
      }
    })
  } else {
    exportSheetDraft.channels = ['__overall__', ...getExportDrawerChannelValues()]
  }
}

function clearDraftChannels() {
  if (exportSheetDraft.category === 'traffic') {
    exportSheetDraft.channelConfigs = []
  } else {
    exportSheetDraft.channels = []
  }
}

// 流量渠道勾选切换
function toggleTrafficChannel(channel) {
  const idx = exportSheetDraft.channelConfigs.findIndex((c) => c.channel === channel)
  if (idx >= 0) {
    exportSheetDraft.channelConfigs.splice(idx, 1)
  } else {
    exportSheetDraft.channelConfigs.push({
      channel,
      metrics: channel === '__overall__' ? [...DEFAULT_OVERALL_METRICS] : [...DEFAULT_CHANNEL_METRICS]
    })
    // 整体始终在最前
    exportSheetDraft.channelConfigs.sort((a, b) => {
      if (a.channel === '__overall__') return -1
      if (b.channel === '__overall__') return 1
      return 0
    })
  }
}

// 设置所有已选渠道的指标（全局快捷操作）
function setAllChannelsMetrics(metrics) {
  exportSheetDraft.channelConfigs.forEach((cfg) => { cfg.metrics = [...metrics] })
}

// 仅整体用全量，其他渠道用 PV+UV
function setOverallFullOthersBasic() {
  exportSheetDraft.channelConfigs.forEach((cfg) => {
    cfg.metrics = cfg.channel === '__overall__' ? [...DEFAULT_OVERALL_METRICS] : [...DEFAULT_CHANNEL_METRICS]
  })
}

// 检测当前项目在所选日期范围内，停留时长有哪些口径可用
async function detectStayTimeSource() {
  const project = getExportDrawerProject()
  if (!project?.code) {
    ElMessage.warning('请先选择项目')
    return
  }
  const dateRange = exportDateRange.value
  if (!dateRange?.[0] || !dateRange?.[1]) {
    ElMessage.warning('请先选择导出时间范围')
    return
  }
  stayDetecting.value = true
  stayDetectResult.value = null
  try {
    const projectCode = project.code
    const startDate = dateRange[0]
    const endDate = dateRange[1]
    const nextDay = new Date(endDate)
    nextDay.setDate(nextDay.getDate() + 1)
    const nextDayStr = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`
    const sql = `SELECT type, COUNT(user_id) AS pv, COUNT(DISTINCT user_id) AS uv, COUNT(DISTINCT traceid) AS traceid_count FROM mf_tracking_data_origin_record WHERE project_code = '${String(projectCode).replace(/'/g, "''")}' AND t_timestamp >= TO_MILLIS(DATETIME '${startDate} 00:00:00') AND t_timestamp < TO_MILLIS(DATETIME '${nextDayStr} 00:00:00') AND type IN ('timeOnSite') GROUP BY type UNION ALL SELECT 'traceid_check' AS type, COUNT(user_id) AS pv, COUNT(DISTINCT user_id) AS uv, COUNT(DISTINCT traceid) AS traceid_count FROM mf_tracking_data_origin_record WHERE project_code = '${String(projectCode).replace(/'/g, "''")}' AND t_timestamp >= TO_MILLIS(DATETIME '${startDate} 00:00:00') AND t_timestamp < TO_MILLIS(DATETIME '${nextDayStr} 00:00:00') AND traceid IS NOT NULL AND traceid != ''`
    const result = await api.executeDataWorksSql(sql)
    const rows = Array.isArray(result?.data) ? result.data : []
    const timeOnSiteRow = rows.find((r) => r.type === 'timeOnSite')
    const traceIdRow = rows.find((r) => r.type === 'traceid_check')
    const hasTimeOnSite = timeOnSiteRow && toNumber(timeOnSiteRow.pv) > 0
    const hasTraceId = traceIdRow && toNumber(traceIdRow.traceid_count) > 1
    stayDetectResult.value = {
      hasTimeOnSite,
      hasTraceId,
      timeOnSitePv: timeOnSiteRow ? toNumber(timeOnSiteRow.pv) : 0,
      traceIdCount: traceIdRow ? toNumber(traceIdRow.traceid_count) : 0,
      recommend: hasTimeOnSite ? 'dataworks' : (hasTraceId ? 'traceId' : 'dataworks')
    }
    // 自动应用推荐
    if (hasTimeOnSite) {
      exportForm.stayTimeSource = 'dataworks'
    } else if (hasTraceId) {
      exportForm.stayTimeSource = 'traceId'
    }
    ElMessage.success(`检测完成，推荐使用：${stayDetectResult.value.recommend === 'dataworks' ? 'type=timeOnSite' : 'traceId 计算'}`)
  } catch (err) {
    console.error('停留时长检测失败:', err)
    ElMessage.error('检测失败：' + (err.message || '查询异常'))
  } finally {
    stayDetecting.value = false
  }
}

function selectAllDraftBehaviors() {
  exportSheetDraft.searchSelected = Array.from(new Set([
    ...exportSheetDraft.searchSelected,
    ...exportBehaviorOptions.value.map((item) => item.value)
  ]))
}

function clearDraftBehaviors() {
  exportSheetDraft.behaviors = []
  exportSheetDraft.mergedBehaviors = []
  exportSheetDraft.searchSelected = []
  exportSheetDraft.mergeName = ''
  exportSheetDraft.editingGroupIdx = null
}

// 搜索结果里的勾选切换
function toggleDraftBehavior(name) {
  const idx = exportSheetDraft.searchSelected.indexOf(name)
  if (idx >= 0) {
    exportSheetDraft.searchSelected.splice(idx, 1)
  } else {
    exportSheetDraft.searchSelected.push(name)
  }
}

// 将当前 searchSelected 加入独立行为
async function addSelectedToSheet() {
  if (!exportSheetDraft.searchSelected.length) {
    ElMessage.warning('请先在下方勾选行为')
    return
  }
  const newItems = exportSheetDraft.searchSelected.filter(
    (b) => !exportSheetDraft.behaviors.includes(b)
  )
  exportSheetDraft.behaviors.push(...newItems)
  exportSheetDraft.behaviors = sortDraftBehaviorList(exportSheetDraft.behaviors)
  exportSheetDraft.searchSelected = []
  exportSheetDraft.behaviorKeyword = ''
  ElMessage.success(`已加入 ${newItems.length} 个独立行为`)
  // 重新加载全部行为数据（排除规则可能变化）
  exportBehaviorSearchLoading.value = true
  try {
    await loadExportBehaviorOptions('')
  } finally {
    exportBehaviorSearchLoading.value = false
  }
}

// 将当前 searchSelected 合并为组（或更新已有组）
async function commitMergeGroup() {
  if (!exportSheetDraft.searchSelected.length) {
    ElMessage.warning('请先在下方勾选要合并的行为')
    return
  }
  if (exportSheetDraft.searchSelected.length < 2) {
    ElMessage.warning('合并组至少需要 2 个行为')
    return
  }
  const mergeName = exportSheetDraft.mergeName.trim()
  if (!mergeName) {
    ElMessage.warning('请填写合并组名称')
    return
  }
  const editIdx = exportSheetDraft.editingGroupIdx
  if (editIdx !== null && exportSheetDraft.mergedBehaviors[editIdx]) {
    const group = exportSheetDraft.mergedBehaviors[editIdx]
    group.behaviors = sortDraftBehaviorList([...exportSheetDraft.searchSelected])
    group.name = mergeName
    ElMessage.success(`已更新合并组「${group.name}」`)
  } else {
    exportSheetDraft.mergedBehaviors.push({
      name: mergeName,
      behaviors: sortDraftBehaviorList([...exportSheetDraft.searchSelected]),
      keepOriginal: false
    })
    ElMessage.success(`已创建合并组「${mergeName}」`)
  }
  exportSheetDraft.searchSelected = []
  exportSheetDraft.mergeName = ''
  exportSheetDraft.editingGroupIdx = null
  mergeGroupEditBaseline.value = ''
  exportSheetDraft.behaviorKeyword = ''
  exportBehaviorSearchLoading.value = true
  try {
    await loadExportBehaviorOptions('')
  } finally {
    exportBehaviorSearchLoading.value = false
  }
}

function getMergedGroupBehaviors(idx, editingIdx = exportSheetDraft.editingGroupIdx) {
  const group = exportSheetDraft.mergedBehaviors[idx]
  if (!group) return []
  if (editingIdx === idx) {
    return [...exportSheetDraft.searchSelected]
  }
  return [...(group.behaviors || [])]
}

function serializeMergeGroupEditState(idx) {
  const group = exportSheetDraft.mergedBehaviors[idx]
  if (!group) return ''
  return JSON.stringify({
    name: exportSheetDraft.mergeName || group.name || '',
    behaviors: [...getMergedGroupBehaviors(idx)],
    keepOriginal: !!group.keepOriginal
  })
}

function isMergeGroupEditDirty() {
  const idx = exportSheetDraft.editingGroupIdx
  if (idx === null || !mergeGroupEditBaseline.value) return false
  return serializeMergeGroupEditState(idx) !== mergeGroupEditBaseline.value
}

async function handleMergeGroupCardClick(idx) {
  if (exportSheetDraft.editingGroupIdx === idx) return
  if (exportSheetDraft.editingGroupIdx !== null && isMergeGroupEditDirty()) {
    try {
      await ElMessageBox.confirm(
        '当前合并组有未保存的修改，切换后会丢失，是否继续？',
        '提示',
        {
          confirmButtonText: '保存并切换',
          cancelButtonText: '直接切换（丢弃）',
          distinguishCancelAndClose: true,
          type: 'warning'
        }
      )
      await saveMergeGroupEdit()
    } catch (action) {
      if (action === 'cancel') {
        cancelMergeGroupEdit()
      } else {
        return
      }
    }
  } else if (exportSheetDraft.editingGroupIdx !== null) {
    cancelMergeGroupEdit()
  }
  startEditGroup(idx)
}

// 点击合并组"编辑"→ 回显该组已选内容到 searchSelected，并设置编辑模式
function startEditGroup(idx) {
  const group = exportSheetDraft.mergedBehaviors[idx]
  if (!group) return
  exportSheetDraft.editingGroupIdx = idx
  exportSheetDraft.mergeName = group.name || ''
  exportSheetDraft.searchSelected = [...(group.behaviors || [])]
  mergeGroupEditBaseline.value = serializeMergeGroupEditState(idx)
  ElMessage.info(`正在编辑合并组「${group.name || `组 ${idx + 1}`}」，修改后点「保存」`)
}

async function saveMergeGroupEdit() {
  const idx = exportSheetDraft.editingGroupIdx
  if (idx === null) return
  const group = exportSheetDraft.mergedBehaviors[idx]
  if (!group) return
  const name = exportSheetDraft.mergeName.trim() || group.name?.trim()
  if (!name) {
    ElMessage.warning('请填写合并组名称')
    return
  }
  const behaviors = [...getMergedGroupBehaviors(idx)]
  if (behaviors.length < 2) {
    ElMessage.warning('合并组至少需要 2 个行为')
    return
  }
  group.name = name
  group.behaviors = sortDraftBehaviorList(behaviors)
  exportSheetDraft.mergeName = ''
  exportSheetDraft.searchSelected = []
  exportSheetDraft.editingGroupIdx = null
  mergeGroupEditBaseline.value = ''
  ElMessage.success(`合并组「${name}」已保存`)
  exportBehaviorSearchLoading.value = true
  try {
    await loadExportBehaviorOptions(exportSheetDraft.behaviorKeyword || '')
  } finally {
    exportBehaviorSearchLoading.value = false
  }
}

// 取消编辑模式
function cancelEditGroup() {
  cancelMergeGroupEdit()
}

function cancelMergeGroupEdit() {
  const idx = exportSheetDraft.editingGroupIdx
  if (idx === null) return
  try {
    const baseline = JSON.parse(mergeGroupEditBaseline.value || '{}')
    const group = exportSheetDraft.mergedBehaviors[idx]
    if (group) {
      if (baseline.name !== undefined) group.name = baseline.name
      if (Array.isArray(baseline.behaviors)) group.behaviors = [...baseline.behaviors]
      if (baseline.keepOriginal !== undefined) group.keepOriginal = baseline.keepOriginal
    }
  } catch { /* ignore */ }
  exportSheetDraft.editingGroupIdx = null
  exportSheetDraft.mergeName = ''
  exportSheetDraft.searchSelected = []
  mergeGroupEditBaseline.value = ''
}

function removeDraftBehavior(name) {
  exportSheetDraft.behaviors = exportSheetDraft.behaviors.filter((item) => item !== name)
}

function addMergedBehavior() {
  exportSheetDraft.mergedBehaviors.push({ name: '', behaviors: [], keepOriginal: false })
}

function removeMergedBehavior(idx) {
  exportSheetDraft.mergedBehaviors.splice(idx, 1)
  if (exportSheetDraft.editingGroupIdx === idx) {
    cancelMergeGroupEdit()
  } else if (exportSheetDraft.editingGroupIdx !== null && exportSheetDraft.editingGroupIdx > idx) {
    exportSheetDraft.editingGroupIdx -= 1
  }
}

function toggleBehaviorInMerge(mergedIdx, behaviorName) {
  const group = exportSheetDraft.mergedBehaviors[mergedIdx]
  if (!group) return
  group.behaviors = group.behaviors.filter((b) => b !== behaviorName)
  if (exportSheetDraft.editingGroupIdx === mergedIdx) {
    exportSheetDraft.searchSelected = exportSheetDraft.searchSelected.filter((b) => b !== behaviorName)
  }
  if (!group.behaviors.length) {
    removeMergedBehavior(mergedIdx)
  }
}

function removeDraftChannel(channel) {
  exportSheetDraft.channels = exportSheetDraft.channels.filter((item) => item !== channel)
}

function resetExportSheetDraft(category = 'traffic', initialChannels = []) {
  exportSheetDraft.category = category
  exportSheetDraft.sheetName = category === 'behavior'
    ? `行为数据_${exportSheets.value.filter((item) => item.category === 'behavior').length + 1}`
    : `流量数据_${exportSheets.value.filter((item) => item.category === 'traffic').length + 1}`

  if (category === 'traffic') {
    const allChannels = ['__overall__', ...initialChannels]
    exportSheetDraft.channelConfigs = allChannels.map((ch) => ({
      channel: ch,
      metrics: ch === '__overall__' ? [...DEFAULT_OVERALL_METRICS] : [...DEFAULT_CHANNEL_METRICS]
    }))
  } else {
    exportSheetDraft.channelConfigs = [{ channel: '__overall__', metrics: [...DEFAULT_OVERALL_METRICS] }]
  }

  exportSheetDraft.channels = ['__overall__']
  exportSheetDraft.behaviorKeyword = ''
  exportSheetDraft.behaviors = []
  exportSheetDraft.sortBehaviors = true
  exportSheetDraft.mergedBehaviors = []
  exportSheetDraft.searchSelected = []
  exportSheetDraft.mergeName = ''
  exportSheetDraft.editingGroupIdx = null
  mergeGroupEditBaseline.value = ''
  exportBehaviorPool.value = []
  captureExportDraftBaseline()
}

function validateBehaviorSheetContent() {
  const validMergedGroups = exportSheetDraft.mergedBehaviors.filter((m) => m.behaviors?.length)
  if (exportSheetDraft.behaviors.length || validMergedGroups.length) return true
  ElMessage.warning('请至少勾选一个独立行为，或配置一个合并组')
  return false
}

function moveExportSheet(idx, direction) {
  const targetIdx = idx + direction
  if (targetIdx < 0 || targetIdx >= exportSheets.value.length) return
  const arr = [...exportSheets.value]
  const tmp = arr[idx]
  arr[idx] = arr[targetIdx]
  arr[targetIdx] = tmp
  exportSheets.value = arr
}

/**
 * draft 提交：有 editingSheetId = 更新已有 Sheet；无 = 新增到清单
 */
function handleDraftSubmit() {
  if (editingSheetId.value) {
    // 更新模式：把 draft 内容写回到对应 Sheet
    const idx = exportSheets.value.findIndex(s => s.id === editingSheetId.value)
    if (idx < 0) { ElMessage.warning('找不到要更新的 Sheet'); return }
    const sheetName = (exportSheetDraft.sheetName || '').trim()
    if (!sheetName) { ElMessage.warning('请填写 Sheet 名称'); return }
    if (exportSheetDraft.category === 'behavior' && !validateBehaviorSheetContent()) return

    const updated = { ...exportSheets.value[idx], sheetName }
    if (exportSheetDraft.category === 'traffic') {
      updated.channelConfigs = exportSheetDraft.channelConfigs.map(c => ({ ...c, metrics: [...c.metrics] }))
    } else {
      updated.channels = [...exportSheetDraft.channels]
      updated.sortBehaviors = !!exportSheetDraft.sortBehaviors
      updated.behaviors = [...exportSheetDraft.behaviors]
      updated.mergedBehaviors = exportSheetDraft.mergedBehaviors.filter(m => m.behaviors?.length).map(m => ({
        name: m.name || m.behaviors.join('+'), behaviors: [...m.behaviors], keepOriginal: !!m.keepOriginal
      }))
      Object.assign(updated, finalizeBehaviorSheetConfig(updated))
    }
    exportSheets.value.splice(idx, 1, updated)
    ElMessage.success(`「${sheetName}」已更新`)
    cancelEditSheet()
  } else {
    appendExportSheet()
  }
}

function cancelEditSheet() {
  editingSheetId.value = null
  // 重置 draft 到空状态
  const allChannelValues = getExportDrawerChannelValues()
  resetExportSheetDraft(exportSheetDraft.category, allChannelValues)
}

function appendExportSheet() {
  const sheetName = (exportSheetDraft.sheetName || '').trim()
  if (!sheetName) {
    ElMessage.warning('请填写 sheet 名称')
    return
  }
  if (!exportSheetDraft.channelConfigs.length && exportSheetDraft.category === 'traffic') {
    ElMessage.warning('请至少选择一个渠道')
    return
  }
  if (!exportSheetDraft.channels.length && exportSheetDraft.category === 'behavior') {
    ElMessage.warning('请至少选择一个渠道对象')
    return
  }
  if (exportSheetDraft.category === 'behavior' && !validateBehaviorSheetContent()) {
    return
  }

  // 独立行为直接用 behaviors，合并组用 mergedBehaviors
  const validMergedGroups = exportSheetDraft.mergedBehaviors.filter((m) => m.behaviors?.length)

  const sheetEntry = {
    id: `sheet_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    category: exportSheetDraft.category,
    sheetName,
    channelConfigs: exportSheetDraft.category === 'traffic'
      ? exportSheetDraft.channelConfigs.map((c) => ({ channel: c.channel, metrics: [...c.metrics] }))
      : [],
    channels: exportSheetDraft.category === 'behavior' ? [...exportSheetDraft.channels] : [],
    behaviors: exportSheetDraft.category === 'behavior' ? [...exportSheetDraft.behaviors] : [],
    sortBehaviors: exportSheetDraft.category === 'behavior'
      ? !!exportSheetDraft.sortBehaviors
      : undefined,
    mergedBehaviors: exportSheetDraft.category === 'behavior'
      ? validMergedGroups.map((m) => ({
          name: m.name || m.behaviors.join('+'),
          behaviors: [...m.behaviors],
          keepOriginal: !!m.keepOriginal
        }))
      : [],
    metrics: [...exportForm.selectedMetrics],
    noCumDailyKeys: ['sumStayTime', 'avgStayTime']
  }
  exportSheets.value.push(
    exportSheetDraft.category === 'behavior'
      ? finalizeBehaviorSheetConfig(sheetEntry)
      : sheetEntry
  )
  resetExportSheetDraft(exportSheetDraft.category, getExportDrawerChannelValues())
  if (exportSheetDraft.category === 'behavior') {
    void loadExportBehaviorOptions('')
  }
}

function removeExportSheet(id) {
  exportSheets.value = exportSheets.value.filter((item) => item.id !== id)
}

function formatExportTargets(targets = []) {
  return targets.map((item) => (item === '__overall__' ? '整体' : item)).join('、')
}

function formatBehaviorPreview(behaviors = []) {
  if (!behaviors.length) return '-'
  if (behaviors.length <= 3) return behaviors.join('、')
  return `${behaviors.slice(0, 3).join('、')} 等 ${behaviors.length} 项`
}

function handleExportBehaviorKeywordSearch(query) {
  clearExportBehaviorSearchTimer()
  exportBehaviorSearchLoading.value = true
  exportBehaviorSearchTimer = window.setTimeout(async () => {
    exportSheetDraft.behaviorKeyword = query || ''
    try {
      await loadExportBehaviorOptions(query || '')
    } finally {
      exportBehaviorSearchLoading.value = false
    }
  }, 280)
}

async function runExportBehaviorSearch() {
  clearExportBehaviorSearchTimer()
  exportBehaviorSearchLoading.value = true
  try {
    await loadExportBehaviorOptions(exportSheetDraft.behaviorKeyword || '')
  } finally {
    exportBehaviorSearchLoading.value = false
  }
}

async function handleExportBehaviorKeywordClear() {
  clearExportBehaviorSearchTimer()
  exportSheetDraft.behaviorKeyword = ''
  exportBehaviorSearchLoading.value = true
  try {
    await loadExportBehaviorOptions('')
  } finally {
    exportBehaviorSearchLoading.value = false
  }
}

async function loadExportBehaviorOptions(keyword = '') {
  const project = getExportDrawerProject()
  if (!project?.id) {
    exportBehaviorPool.value = []
    return
  }
  const result = await fetchBehaviorList({
    ...buildExportBaseParams(exportDateRange.value),
    channel: '',
    indexName: keyword
  })
  exportBehaviorPool.value = normalizeBehaviorList(result)
}

async function handleExport() {
  const project = getExportDrawerProject()
  if (!project?.id) {
    ElMessage.warning('请先选择项目')
    return
  }
  if (!exportSheets.value.length) {
    ElMessage.warning('请先加入至少一个导出 sheet')
    return
  }
  lastExportAction.value = 'mixed'
  exporting.value = true
  startExportProgress()

  // 预存导出记录（所有导出都保存，无模板时 templateAlias 为"无模板临时导出"）
  let recordId = null
  try {
    const tplAlias = drawerCurrentTemplateId.value
      ? (drawerTemplates.value.find(t => t.id === drawerCurrentTemplateId.value)?.alias || '模板导出')
      : '无模板临时导出'
    recordId = await createExportRecord({
      templateId:    drawerCurrentTemplateId.value || null,
      templateAlias: tplAlias,
      projectCode:   project.code,
      projectName:   project.name,
      dateStart:     exportDateRange.value[0],
      dateEnd:       exportDateRange.value[1],
      configJson:    buildExportConfigSnapshot()
    })
    currentExportRecordId.value = recordId
    if (recordId) await markRecordRunning(recordId)
  } catch (e) {
    console.warn('创建导出记录失败（不影响导出）:', e.message)
  }

  try {
    const sheets = await exportConfiguredWorkbook()
    exportDrawerVisible.value = false
    finishExportProgress('success', '导出完成')
    ElMessage.success('导出成功')
    // 写入成功结果（去掉 theme 对象，只保留重建 Excel 所需的数据）
    if (recordId) {
      const sheetsToStore = sheets.map(s => ({
        name:       s.name,
        rows:       s.rows,
        merges:     s.merges,
        colWidths:  s.colWidths,
        rowHeights: s.rowHeights,
        themeKey:   exportForm.themeKey || 'ocean'
      }))
      try {
        await markRecordDone(recordId, sheetsToStore)
      } catch (e) {
        console.warn('写入导出记录失败:', e.message)
      }
    }
  } catch (error) {
    console.error(error)
    finishExportProgress('exception', error.message || '导出失败')
    ElMessage.error(error.message || '导出失败')
    if (recordId) {
      markRecordFailed(recordId, error.message || String(error)).catch(() => {})
    }
  } finally {
    exporting.value = false
  }
}

async function exportConfiguredWorkbook() {
  const sheets = []
  const dates = buildDateList(exportDateRange.value[0], exportDateRange.value[1])
  exportProgressCtrl = createExportProgressController(exportProgress)
  exportProgressCtrl.start(exportSheets.value, `准备导出 ${exportSheets.value.length} 个 Sheet`)

  for (const sheetConfig of exportSheets.value) {
    if (sheetConfig.category === 'traffic') {
      sheets.push(await buildTrafficExportSheet(sheetConfig, dates, []))
    } else {
      sheets.push(await buildBehaviorExportSheet(sheetConfig, dates, ['pv', 'uv']))
    }
  }

  exportProgressCtrl.report('正在组装 Excel 工作簿...', { label: '生成 Excel' })
  exportProgressCtrl.stepDone('工作表结构已生成', '生成 Excel')

  const project = getExportDrawerProject()
  exportWorkbook({
    sheets,
    fileName: buildExportFileName({
      projectName: project.name,
      projectCode: project.code,
      dataLabel: exportSheets.value.length > 1 ? '组合数据' : exportSheets.value[0]?.category === 'behavior' ? '行为数据' : '流量数据',
      startDate: exportDateRange.value[0],
      endDate: exportDateRange.value[1]
    })
  })
  return sheets
}

async function buildTrafficExportSheet(sheetConfig, dates, metrics) {
  // 优先用 channelConfigs（新格式），兼容旧格式 channels+metrics
  const channelConfigs = sheetConfig.channelConfigs?.length
    ? sheetConfig.channelConfigs
    : (sheetConfig.channels || ['__overall__']).map((ch) => ({ channel: ch, metrics: [...(sheetConfig.metrics || metrics)] }))

  const sortedConfigs = sortTrafficTargets(channelConfigs.map((c) => c.channel))
    .map((ch) => channelConfigs.find((c) => c.channel === ch))
    .filter(Boolean)

  // 收集所有出现过的指标（用于构建 pivot 表头）
  const allMetricKeys = Array.from(new Set(sortedConfigs.flatMap((c) => c.metrics)))
  const allStayMetrics = allMetricKeys.filter((m) => ['sumStayTime', 'avgStayTime'].includes(m))
  const valueMap = {}
  const items = []

  for (const cfg of sortedConfigs) {
    const { channel, metrics: channelMetrics } = cfg
    const label = channel === '__overall__' ? '整体' : channel
    items.push(label)

    const stayMetrics = channelMetrics.filter((m) => ['sumStayTime', 'avgStayTime'].includes(m))
    const normalMetrics = channelMetrics.filter((m) => !['sumStayTime', 'avgStayTime'].includes(m))

    const metricsLabel = channelMetrics.map(m => (
      { pv:'PV', uv:'UV', clickUser:'点击人数', clickCount:'点击次数', sumStayTime:'总停留', avgStayTime:'均停留', clickRate:'点击率', dropRate:'流失率' }[m] || m
    )).join(' + ')
    stepExportProgress(
      `[接口] ${sheetConfig.sheetName} · ${label} → 查询 ${metricsLabel}`,
      false,
      `${sheetConfig.sheetName} / ${label}`
    )
    const params = buildExportBaseParams(exportDateRange.value)
    if (channel !== '__overall__') params.channel = channel
    const result = normalizeTraffic(await fetchBasicMain(params), exportDateRange.value)

    valueMap[label] = {}
    dates.forEach((date) => {
      valueMap[label][date] = {}
      // 只初始化该渠道自己选择的指标，其他指标不设置（保持 undefined，渲染时显示 -）
      channelMetrics.forEach((m) => { valueMap[label][date][m] = 0 })
    })
    ;(result.dailyBasics || []).forEach((item) => {
      normalMetrics.forEach((m) => {
        if (item[m] !== undefined) valueMap[label][item.date][m] = toNumber(item[m])
      })
    })

    if (stayMetrics.length) {
      const staySource = exportForm.stayTimeSource === 'traceId' ? 'traceId(MAX-MIN)' : 'type=timeOnSite'
      stepExportProgress(
        `[DataWorks] ${sheetConfig.sheetName} · ${label} → 查询停留时长（${staySource}）`,
        false,
        `${sheetConfig.sheetName} / ${label} 停留时长`
      )
      try {
        const exportProject = getExportDrawerProject()
        const projectCode = exportProject?.code
        const version = exportProject?.version || 'v4'
        const [startDate, endDate] = exportDateRange.value
        const byChannel = channel !== '__overall__'
        const staySql = exportForm.stayTimeSource === 'traceId'
          ? buildTraceStayTimeSql({ projectCode, version, startDate, endDate, mode: 'daily', byChannel, stayAvgBase: exportForm.stayAvgBase })
          : buildStayTimeSql({ projectCode, version, startDate, endDate, mode: 'daily', byChannel, stayAvgBase: exportForm.stayAvgBase })
        const stayResult = await api.executeDataWorksSql(staySql)
        const stayRows = Array.isArray(stayResult?.data) ? stayResult.data : []
        stayRows.forEach((row) => {
          const d = row.time_scope
          if (!d || !valueMap[label]?.[d]) return
          const rowChannel = row.channel || ''
          if (byChannel && rowChannel !== channel) return
          if (!byChannel && rowChannel && channel === '__overall__') return
          if (stayMetrics.includes('sumStayTime')) valueMap[label][d].sumStayTime = toNumber(row.totalTimeOnSite || row.timeOnSite || 0)
          if (stayMetrics.includes('avgStayTime')) valueMap[label][d].avgStayTime = toNumber(row.avg_stay_seconds || 0)
        })
      } catch (err) {
        console.error('停留时长查询失败:', err)
      }
    }
    stepExportProgress(`已完成：${sheetConfig.sheetName} / ${label}`, true, `${sheetConfig.sheetName} / ${label}`)
  }

  stepExportProgress(`正在组装 Sheet：${sheetConfig.sheetName}`, false, sheetConfig.sheetName)
  const sheet = buildPivotSheet({
    sheetName: sheetConfig.sheetName,
    orientation: exportForm.orientation,
    dateList: dates,
    metrics: allMetricKeys,
    items,
    valueMap,
    themeKey: exportForm.themeKey,
    includeSumRow: exportForm.includeSummaryRow && !exportForm.summaryDedup && !exportForm.summaryCumDaily || false,
    includeDailySumRow: exportForm.includeSummaryRow && exportForm.summaryCumDaily || false,
    includeDedupRow: exportForm.includeSummaryRow && exportForm.summaryDedup || false,
    noCumDailyKeys: ['sumStayTime', 'avgStayTime'],
    // per-item metrics：每个渠道只展示自己配置的指标，其他列填 -
    itemMetrics: Object.fromEntries(
      sortedConfigs.map(cfg => [
        cfg.channel === '__overall__' ? '整体' : cfg.channel,
        cfg.metrics
      ])
    )
  })
  stepExportProgress(`Sheet 已生成：${sheetConfig.sheetName}`, true, sheetConfig.sheetName)
  return sheet
}

async function buildBehaviorExportSheet(sheetConfig, dates, metrics) {
  const targets = sortTrafficTargets(sheetConfig.channels.length ? [...sheetConfig.channels] : ['__overall__'])
  const exportBehaviors = sheetConfig.behaviors || []
  const mergedGroups = (sheetConfig.mergedBehaviors || []).filter((m) => m.behaviors?.length)
  const items = []
  const valueMap = {}

  // 独立行为：走接口逐个查询
  for (const target of targets) {
    const channelLabel = target === '__overall__' ? '整体' : target
    for (const behaviorName of exportBehaviors) {
      const itemLabel = targets.length > 1 ? `${channelLabel} / ${behaviorName}` : behaviorName
      items.push(itemLabel)
      valueMap[itemLabel] = {}
      dates.forEach((date) => { valueMap[itemLabel][date] = { pv: 0, uv: 0 } })
      stepExportProgress(`[接口] ${sheetConfig.sheetName} · 行为「${behaviorName}」${targets.length > 1 ? `/ ${channelLabel}` : ''} → 查询 PV/UV`, false, `${sheetConfig.sheetName} / ${itemLabel}`)
      const params = {
        ...buildExportBaseParams(exportDateRange.value),
        indexName: behaviorName,
        channel: target === '__overall__' ? '' : target
      }
      const result = normalizeBehaviorDetail(await fetchBehaviorDetail(params), exportDateRange.value)
      ;(result.dailyCustoms || []).forEach((item) => {
        valueMap[itemLabel][item.date] = { pv: toNumber(item.pv), uv: toNumber(item.uv) }
      })
      stepExportProgress(`已完成：${sheetConfig.sheetName} / ${itemLabel}`, true, `${sheetConfig.sheetName} / ${itemLabel}`)
    }
  }

  // 合并组：走 DataWorks SQL IN 查询
  for (const merged of mergedGroups) {
    const mergeName = merged.name || merged.behaviors.join('+')

    // 若该组 keepOriginal=true，先单独导出各成员行（走接口）
    if (merged.keepOriginal) {
      for (const target of targets) {
        const channelLabel = target === '__overall__' ? '整体' : target
        for (const behaviorName of merged.behaviors || []) {
          const itemLabel = targets.length > 1 ? `${channelLabel} / ${behaviorName}` : behaviorName
          if (!items.includes(itemLabel)) {
            items.push(itemLabel)
            valueMap[itemLabel] = {}
            dates.forEach((date) => { valueMap[itemLabel][date] = { pv: 0, uv: 0 } })
            stepExportProgress(`[接口] ${sheetConfig.sheetName} · 组内原始「${behaviorName}」→ 查询 PV/UV`, false, `${sheetConfig.sheetName} / ${itemLabel}`)
            const params = {
              ...buildExportBaseParams(exportDateRange.value),
              indexName: behaviorName,
              channel: target === '__overall__' ? '' : target
            }
            const result = normalizeBehaviorDetail(await fetchBehaviorDetail(params), exportDateRange.value)
            ;(result.dailyCustoms || []).forEach((item) => {
              valueMap[itemLabel][item.date] = { pv: toNumber(item.pv), uv: toNumber(item.uv) }
            })
            stepExportProgress(`已完成：${sheetConfig.sheetName} / ${itemLabel}`, true, `${sheetConfig.sheetName} / ${itemLabel}`)
          }
        }
      }
    }

    // 合并结果行：走 DataWorks
    const mergeLabel = mergeName
    items.push(mergeLabel)
    valueMap[mergeLabel] = {}
    dates.forEach((date) => { valueMap[mergeLabel][date] = { pv: 0, uv: 0 } })
    stepExportProgress(`[DataWorks] ${sheetConfig.sheetName} · 合并埋点「${mergeLabel}」→ UV去重查询（${merged.behaviors.length}个埋点）`, false, `${sheetConfig.sheetName} / ${mergeLabel}`)

    try {
      const exportProject = getExportDrawerProject()
      const projectCode = exportProject?.code
      const version = exportProject?.version || 'v4'
      const [startDate, endDate] = exportDateRange.value
      const inList = merged.behaviors.map((b) => "'".concat(String(b).replace(/'/g, "''"), "'")).join(',')
      const nextDay = new Date(endDate)
      nextDay.setDate(nextDay.getDate() + 1)
      const nextDayStr = formatDate(nextDay)
      const sql = `SELECT TO_CHAR(FROM_UNIXTIME(t_timestamp / 1000),'yyyy-mm-dd') AS time_scope, COUNT(user_id) AS pv, COUNT(DISTINCT user_id) AS uv FROM mf_tracking_data_origin_record WHERE project_code = '${String(projectCode).replace(/'/g, "''")}' AND custom_type IN (${inList}) AND t_timestamp >= TO_MILLIS(DATETIME '${startDate} 00:00:00') AND t_timestamp < TO_MILLIS(DATETIME '${nextDayStr} 00:00:00') GROUP BY time_scope`
      const result = await api.executeDataWorksSql(sql)
      const rows = Array.isArray(result?.data) ? result.data : []
      rows.forEach((row) => {
        const d = row.time_scope
        if (d && valueMap[mergeLabel]?.[d]) {
          valueMap[mergeLabel][d].pv = toNumber(row.pv)
          valueMap[mergeLabel][d].uv = toNumber(row.uv)
        }
      })
    } catch (err) {
      console.error('合并行为查询失败:', err)
    }
    stepExportProgress(`已完成：${sheetConfig.sheetName} / ${mergeLabel}（DataWorks）`, true, `${sheetConfig.sheetName} / ${mergeLabel}`)
  }

  stepExportProgress(`正在组装 Sheet：${sheetConfig.sheetName}`, false, sheetConfig.sheetName)
  const sheet = buildPivotSheet({
    sheetName: sheetConfig.sheetName,
    orientation: exportForm.orientation,
    dateList: dates,
    metrics,
    items,
    valueMap,
    themeKey: exportForm.themeKey,
    includeSumRow: exportForm.includeSummaryRow && !exportForm.summaryDedup && !exportForm.summaryCumDaily || false,
    includeDailySumRow: exportForm.includeSummaryRow && exportForm.summaryCumDaily || false,
    includeDedupRow: exportForm.includeSummaryRow && exportForm.summaryDedup || false,
    noCumDailyKeys: sheetConfig.noCumDailyKeys || []
  })
  stepExportProgress(`Sheet 已生成：${sheetConfig.sheetName}`, true, sheetConfig.sheetName)
  return sheet
}

function ensureExportChannelDefaults() {
  if (!Array.isArray(exportSheetDraft.channels) || !exportSheetDraft.channels.length) {
    exportSheetDraft.channels = ['__overall__']
  }
}

function sortBehaviorNames(list = []) {
  return [...list].sort((a, b) => String(a || '').localeCompare(String(b || ''), 'en'))
}

function sortDraftBehaviorList(list = []) {
  if (!exportSheetDraft.sortBehaviors) return [...list]
  return sortBehaviorNames(list)
}

function applySortBehaviorsToDraft() {
  if (!exportSheetDraft.sortBehaviors) return
  const sorted = finalizeBehaviorSheetConfig({
    category: 'behavior',
    sortBehaviors: true,
    behaviors: exportSheetDraft.behaviors,
    mergedBehaviors: exportSheetDraft.mergedBehaviors
  })
  exportSheetDraft.behaviors = [...sorted.behaviors]
  exportSheetDraft.mergedBehaviors = sorted.mergedBehaviors.map((m) => ({ ...m, behaviors: [...m.behaviors] }))
}

function handleSortBehaviorsChange(checked) {
  if (checked && exportSheetDraft.category === 'behavior') {
    applySortBehaviorsToDraft()
  }
}

function sortTrafficTargets(list = []) {
  return [...list].sort((a, b) => {
    if (a === '__overall__') return -1
    if (b === '__overall__') return 1
    return String(a || '').localeCompare(String(b || ''), 'en')
  })
}

function resetExportDrawerState() {
  exportSheets.value = []
  exportBehaviorPool.value = []
  exportChannelList.value = []
  exportDrawerProject.value = null
  exportDraftBaseline.value = ''
  stayDetectResult.value = null
  drawerSelectedTemplateId.value = null
  drawerCurrentTemplateId.value = null
  editingSheetId.value = null
  exportDateRange.value = Array.isArray(dateRange.value) ? [...dateRange.value] : getDefaultRange()
  resetExportSheetDraft(activeTab.value === 'behavior' ? 'behavior' : 'traffic', [])
}

function backToProjectList() {
  viewMode.value = 'list'
  nextTick(() => {
    document.querySelector('.project-browser')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

async function handleTopChannelChange() {
  if (!currentProject.value) return
  await refreshAll()
}

function openProjectExport(project) {
  projectExportProject.value = project
  projectExportVisible.value = true
}

// ─── 模板保存相关 ──────────────────────────────────────────

function openTemplateSaveDialog() {
  if (!exportSheets.value.length) {
    ElMessage.warning('请先加入至少一个导出 sheet 再保存模板')
    return
  }
  templateSaveVisible.value = true
}

function buildExportConfigSnapshot() {
  return {
    exportForm: {
      orientation:     exportForm.orientation,
      themeKey:        exportForm.themeKey,
      includeSummaryRow: exportForm.includeSummaryRow,
      summaryDedup:    exportForm.summaryDedup,
      summaryCumDaily: exportForm.summaryCumDaily,
      stayTimeSource:  exportForm.stayTimeSource,
      stayAvgBase:     exportForm.stayAvgBase,
      selectedMetrics: exportForm.selectedMetrics
    },
    exportSheets: exportSheets.value.map(s => ({ ...s })),
    exportDateRange: exportDateRange.value
  }
}

function handleTemplateSaved({ templateId } = {}) {
  if (templateId) {
    drawerCurrentTemplateId.value = templateId
    drawerSelectedTemplateId.value = templateId
  }
  loadDrawerTemplates()
  ElMessage.success(templateId
    ? '模板已保存，后续「保存配置」将覆盖更新该模板'
    : '模板已保存，下次可从「从模板载入」快速选用')
}

async function resolveExportDrawerProjectFromRecord(record) {
  if (record.project_code) {
    const matched = projectOptions.value.find((p) => p.code === record.project_code)
    if (matched) {
      exportDrawerProject.value = matched
      return
    }
    const pid = record.project_id || await resolveProjectIdByCode(record.project_code)
    if (pid) {
      exportDrawerProject.value = {
        id: pid,
        code: record.project_code,
        name: record.project_name,
        version: record.project_version || 'v4'
      }
      return
    }
  }
  if (currentProject.value) {
    exportDrawerProject.value = currentProject.value
  } else if (projectExportProject.value) {
    exportDrawerProject.value = projectExportProject.value
  }
}


function normalizeVersion(version) {
  const text = String(version || 'v4').toLowerCase()
  return text === 'v3' ? 'V3' : 'V4'
}

function setTrackingCheckProgress(percentage, message) {
  trackingCheckProgress.percentage = percentage
  trackingCheckProgress.message = message
}

async function openTrackingCheck(project) {
  if (!project?.code) return
  trackingCheckProject.value = project
  trackingCheckVisible.value = true
  trackingCheckLoadingId.value = project.id
  trackingCheckRows.value = []
  trackingCheckSummary.value = []
  trackingOtherRows.value = []
  trackingSuggestions.value = []
  await runTrackingCheck(project)
}

async function rerunTrackingCheck() {
  if (!trackingCheckProject.value) return
  await runTrackingCheck(trackingCheckProject.value)
}

async function runTrackingCheck(project) {
  trackingChecking.value = true
  trackingCheckLoadingId.value = project.id
  try {
    const checkDate = new Date()
    trackingCheckDate.value = formatDate(checkDate)
    setTrackingCheckProgress(10, '生成检测 SQL')
    const sql = buildTrackingCheckSql(project.code, checkDate)
    setTrackingCheckProgress(35, '正在查询今日埋点数据')
    const result = await api.executeDataWorksSql(sql)
    if (result?.success === false) {
      throw new Error(result?.errorMsg || result?.message || '埋点检测查询失败')
    }
    setTrackingCheckProgress(70, '正在分析埋点结果')
    const rows = normalizeTrackingRows(result?.data)
    trackingCheckRows.value = rows
    const analysis = analyzeTrackingRows(rows, normalizeVersion(project.version).toLowerCase())
    trackingCheckSummary.value = analysis.summary
    trackingOtherRows.value = analysis.others
    trackingSuggestions.value = analysis.suggestions
    setTrackingCheckProgress(100, rows.length ? '检查完成' : '今日暂无埋点数据')
  } catch (error) {
    console.error(error)
    trackingSuggestions.value = ['检测请求失败，请确认 SQL 权限、项目 code 或今日数据分区是否正常。']
    setTrackingCheckProgress(100, error.message || '检测失败')
    ElMessage.error(error.message || '检测埋点失败')
  } finally {
    trackingChecking.value = false
    if (trackingCheckLoadingId.value === project.id) {
      trackingCheckLoadingId.value = null
    }
  }
}

function buildTrackingCheckSql(projectCode, checkDate) {
  const year = String(checkDate.getFullYear())
  const month = String(checkDate.getMonth() + 1).padStart(2, '0')
  const day = String(checkDate.getDate()).padStart(2, '0')
  const startTime = `${year}-${month}-${day} 00:00:00`
  const nextDate = new Date(checkDate)
  nextDate.setDate(nextDate.getDate() + 1)
  const nextDay = formatDate(nextDate)
  return `
SELECT
  type,
  customtype,
  page,
  module,
  COUNT(userid) pv,
  COUNT(DISTINCT userid) uv,
  SUM(value) AS value
FROM mf_kafka_tracking
WHERE projectcode IN ('${String(projectCode).replace(/'/g, "\\'")}')
  AND timestamp >= TO_MILLIS(DATETIME '${startTime}')
  AND timestamp < TO_MILLIS(DATETIME '${nextDay} 00:00:00')
  AND year = '${year}'
  AND month = '${month}'
  AND day = '${day}'
GROUP BY type, customtype, page, module
;
`.trim()
}

function normalizeTrackingRows(data) {
  const rows = Array.isArray(data) ? data : []
  return rows.map((item) => ({
    type: item.type || '',
    customtype: item.customtype || '',
    page: item.page || '',
    module: item.module || '',
    pv: toNumber(item.pv),
    uv: toNumber(item.uv),
    value: toNumber(item.value)
  }))
}

function analyzeTrackingRows(rows, version) {
  const matchedRowSet = new Set()
  const summary = TRACKING_CHECK_RULES.map((rule) => {
    const matched = rows.filter((row) => rule.match(version, row))
    matched.forEach((row) => matchedRowSet.add(row))
    const pv = matched.reduce((sum, row) => sum + toNumber(row.pv), 0)
    const uv = matched.reduce((sum, row) => sum + toNumber(row.uv), 0)
    const value = matched.reduce((sum, row) => sum + toNumber(row.value), 0)
    const isHealthy = rule.hasValue ? value > 0 : pv > 0 || uv > 0
    return {
      key: rule.key,
      label: rule.label,
      pv,
      uv,
      value,
      hasValue: rule.hasValue,
      statusText: isHealthy ? '有数据' : '待确认',
      statusType: isHealthy ? 'success' : 'warning',
      tip: buildTrackingTip(rule.key, { pv, uv, value }, version)
    }
  })

  const others = rows.filter((row) => !matchedRowSet.has(row))
  const suggestions = buildTrackingSuggestions(summary, others, version, rows.length)
  return { summary, others, suggestions }
}

function buildTrackingTip(key, values, version) {
  if (key === 'traffic') {
    return version === 'v3'
      ? 'V3 流量主要看 type = flowSource。'
      : 'V4 流量主要看 type = visit。'
  }
  if (key === 'click') {
    return version === 'v3'
      ? 'V3 点击主要看 customtype 非空。'
      : 'V4 点击主要看 type = click。'
  }
  if (key === 'stayTime') {
    return values.value > 0 ? '已检测到停留时长累计值，可继续看均值是否合理。' : '未检测到有效停留时长 value。'
  }
  return '可继续扩展更多规则。'
}

function buildTrackingSuggestions(summary, others, version, totalRows) {
  const suggestions = []
  if (!totalRows) {
    suggestions.push('今天当前项目还没有查到聚合埋点数据，先确认今日是否真的有访问与埋点上报。')
  }
  const traffic = summary.find((item) => item.key === 'traffic')
  const click = summary.find((item) => item.key === 'click')
  const stayTime = summary.find((item) => item.key === 'stayTime')
  if (traffic && traffic.pv === 0 && traffic.uv === 0) {
    suggestions.push(`流量埋点为空，${version === 'v3' ? '重点检查 type=flowSource' : '重点检查 type=visit'} 是否正常上报。`)
  }
  if (click && click.pv === 0 && click.uv === 0) {
    suggestions.push(`点击埋点为空，${version === 'v3' ? '可以先看 customtype 字段是否缺失' : '可以先看 type=click 是否接入'}。`)
  }
  if (stayTime && stayTime.value === 0) {
    suggestions.push('停留时长没有有效 value，建议确认 timeOnSite 的计算结果和上报时机。')
  }
  if (others.length) {
    suggestions.push(`发现 ${others.length} 条未归类埋点类型，后续可以把常用类型补进检测规则。`)
  }
  if (!suggestions.length) {
    suggestions.push('核心埋点项都有数据，接下来可以结合原始聚合结果继续排查 page、module 和 customtype 分布。')
  }
  return suggestions
}

function renderCharts() {
  if (activeTab.value === 'traffic') {
    renderTrafficTrendChart()
    renderTrafficChannelChart()
    return
  }

  if (activeTab.value === 'behavior') {
    renderBehaviorTrendChart()
    renderBehaviorChannelChart()
  }
}

function renderTrafficTrendChart() {
  if (!trafficTrendChartRef.value) return
  if (!trafficTrendChart) trafficTrendChart = echarts.init(trafficTrendChartRef.value)
  const daily = trafficCurrent.value?.dailyBasics || []
  const metric = trafficTrendMetric.value
  const compareDaily = trafficCompare.value?.dailyBasics || []

  trafficTrendChart.setOption(
    {
      color: getThemePalette(activeTheme.value),
      tooltip: { trigger: 'axis' },
      legend: { top: 0 },
      grid: { left: 40, right: 24, top: 48, bottom: 30 },
      xAxis: { type: 'category', data: daily.map((item) => item.date) },
      yAxis: { type: 'value' },
      series: [
        {
          name: `当前${metric.toUpperCase()}`,
          type: 'line',
          smooth: true,
          data: daily.map((item) => toNumber(item[metric])),
          areaStyle: {}
        },
        compareEnabled.value
          ? {
            name: `对比${metric.toUpperCase()}`,
            type: 'line',
            smooth: true,
            lineStyle: { type: 'dashed' },
            data: compareDaily.map((item) => toNumber(item[metric]))
          }
          : null
      ].filter(Boolean)
    },
    true
  )
}

function renderTrafficChannelChart() {
  if (!trafficChannelChartRef.value) return
  if (!trafficChannelChart) trafficChannelChart = echarts.init(trafficChannelChartRef.value)
  const metric = trafficChannelMetric.value
  const rows = trafficChannelRows.value.slice(0, 8)

  // 无数据时清空，避免残留旧图
  if (!rows.length) {
    trafficChannelChart.clear()
    return
  }

  trafficChannelChart.setOption(
    {
      color: getThemePalette(activeTheme.value),
      tooltip: { trigger: 'item' },
      legend: { bottom: 0, type: 'scroll' },
      series: [
        {
          name: metric.toUpperCase(),
          type: 'pie',
          radius: ['40%', '68%'],
          center: ['50%', '46%'],
          label: {
            formatter: '{b}\n{d}%'
          },
          data: rows.map((item) => ({
            name: item.channel,
            value: toNumber(item[metric])
          }))
        }
      ]
    },
    true
  )
}

function renderBehaviorTrendChart() {
  if (!behaviorTrendChartRef.value) return
  if (!behaviorTrendChart) behaviorTrendChart = echarts.init(behaviorTrendChartRef.value)
  const daily = behaviorDetail.value?.dailyCustoms || []
  const compareDaily = behaviorCompareDetail.value?.dailyCustoms || []
  const metric = behaviorTrendMetric.value

  behaviorTrendChart.setOption(
    {
      color: getThemePalette(activeTheme.value),
      tooltip: { trigger: 'axis' },
      legend: { top: 0 },
      grid: { left: 40, right: 24, top: 48, bottom: 30 },
      xAxis: { type: 'category', data: daily.map((item) => item.date) },
      yAxis: { type: 'value' },
      series: [
        {
          name: `当前${metric.toUpperCase()}`,
          type: 'bar',
          barMaxWidth: 24,
          data: daily.map((item) => toNumber(item[metric]))
        },
        compareEnabled.value
          ? {
            name: `对比${metric.toUpperCase()}`,
            type: 'line',
            smooth: true,
            data: compareDaily.map((item) => toNumber(item[metric]))
          }
          : null
      ].filter(Boolean)
    },
    true
  )
}

function renderBehaviorChannelChart() {
  if (!behaviorChannelChartRef.value) return
  if (!behaviorChannelChart) behaviorChannelChart = echarts.init(behaviorChannelChartRef.value)
  const rows = behaviorChannelSource.value?.channelRateList || []

  behaviorChannelChart.setOption(
    {
      color: getThemePalette(activeTheme.value),
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: 40, right: 24, top: 20, bottom: 60 },
      xAxis: {
        type: 'category',
        axisLabel: { rotate: 22 },
        data: rows.map((item) => item.channel)
      },
      yAxis: { type: 'value' },
      series: [
        {
          name: 'PV',
          type: 'bar',
          data: rows.map((item) => toNumber(item.pv))
        },
        {
          name: 'UV',
          type: 'bar',
          data: rows.map((item) => toNumber(item.uv))
        }
      ]
    },
    true
  )
}

function resizeCharts() {
  trafficTrendChart?.resize()
  trafficChannelChart?.resize()
  behaviorTrendChart?.resize()
  behaviorChannelChart?.resize()
}

function disposeCharts() {
  trafficTrendChart?.dispose()
  trafficChannelChart?.dispose()
  behaviorTrendChart?.dispose()
  behaviorChannelChart?.dispose()
  trafficTrendChart = null
  trafficChannelChart = null
  behaviorTrendChart = null
  behaviorChannelChart = null
}

function normalizeTraffic(data, range = dateRange.value) {
  const dateList = buildDateList(range?.[0], range?.[1])
  const dailyMap = new Map()
  ;(Array.isArray(data?.dailyBasics) ? data.dailyBasics : []).forEach((item) => {
    dailyMap.set(item.date, {
      date: item.date,
      channel: item.channel ?? '',
      pv: toNumber(item.pv),
      uv: toNumber(item.uv),
      clickUser: toNumber(item.clickUser),
      clickCount: toNumber(item.clickCount),
      avgStayTime: toNumber(item.avgStayTime),
      sumStayTime: toNumber(item.sumStayTime),
      sevenDayReturnUser: toNumber(item.sevenDayReturnUser),
      clickRate: toNumber(item.clickRate),
      dropRate: toNumber(item.dropRate)
    })
  })
  return {
    pv: toNumber(data?.pv),
    uv: toNumber(data?.uv),
    clickUser: toNumber(data?.clickUser),
    clickCount: toNumber(data?.clickCount),
    clickRate: toNumber(data?.clickRate),
    dropRate: toNumber(data?.dropRate),
    avgStayTime: toNumber(data?.avgStayTime),
    sevenDayReturnUser: toNumber(data?.sevenDayReturnUser),
    dailyBasics: dateList.map((date) => dailyMap.get(date) || ({
      date,
      channel: '',
      pv: 0,
      uv: 0,
      clickUser: 0,
      clickCount: 0,
      avgStayTime: 0,
      sumStayTime: 0,
      sevenDayReturnUser: 0,
      clickRate: 0,
      dropRate: 0
    })),
    channelRateList: normalizeChannelRateList(data?.channelRateList)
  }
}

function normalizeBehaviorList(data) {
  return (Array.isArray(data) ? data : []).map((item) => ({
    ...item,
    indexName: item?.indexName || '',
    pv: toNumber(item?.pv),
    uv: toNumber(item?.uv),
    pvRate: toNumber(item?.pvRate),
    uvRate: toNumber(item?.uvRate)
  }))
}

function normalizeBehaviorDetail(data, range = dateRange.value) {
  const dateList = buildDateList(range?.[0], range?.[1])
  const dailyMap = new Map()
  ;(Array.isArray(data?.dailyCustoms) ? data.dailyCustoms : []).forEach((item) => {
    dailyMap.set(item.date, {
      date: item.date,
      channel: item.channel ?? '',
      indexName: item.indexName || '',
      pv: toNumber(item.pv),
      uv: toNumber(item.uv)
    })
  })
  return {
    pv: toNumber(data?.pv),
    uv: toNumber(data?.uv),
    dailyCustoms: dateList.map((date) => dailyMap.get(date) || ({
      date,
      channel: '',
      indexName: '',
      pv: 0,
      uv: 0
    })),
    channelRateList: normalizeChannelRateList(data?.channelRateList)
  }
}

function normalizeChannelRateList(list) {
  return (Array.isArray(list) ? list : []).map((item) => ({
    channel: item?.channel || '',
    pv: toNumber(item?.pv),
    uv: toNumber(item?.uv)
  }))
}

function createEmptyTraffic() {
  return {
    pv: 0,
    uv: 0,
    clickUser: 0,
    clickCount: 0,
    clickRate: 0,
    dropRate: 0,
    avgStayTime: '0',
    sevenDayReturnUser: 0,
    dailyBasics: [],
    channelRateList: []
  }
}

function createEmptyBehaviorDetail() {
  return {
    pv: 0,
    uv: 0,
    dailyCustoms: [],
    channelRateList: []
  }
}

function buildKpi(key, label, currentValue, compareValue, sub, tagType, isPercent = false) {
  const delta = toNumber(currentValue) - toNumber(compareValue)
  const percent = calcMom(currentValue, compareValue)
  return {
    key,
    label,
    value: isPercent ? formatPercent(currentValue) : formatNumber(currentValue),
    delta,
    percent,
    sub,
    tag: compareEnabled.value ? '对比中' : '当前值',
    tagType
  }
}

function getMetricList(mode) {
  return exportForm.selectedMetrics && exportForm.selectedMetrics.length ? [...exportForm.selectedMetrics] : ['pv', 'uv']
}

function getDefaultRange() {
  const end = getRelativeDate(-1)
  const start = new Date()
  start.setTime(end.getTime())
  start.setDate(start.getDate() - 6)
  return [formatDate(start), formatDate(end)]
}

function getPresetRange(preset) {
  if (preset === 'today') {
    const today = getRelativeDate(0)
    return [formatDate(today), formatDate(today)]
  }

  const end = getRelativeDate(-1)
  const start = new Date(end)
  const sizeMap = {
    yesterday: 1,
    last3: 3,
    last7: 7,
    last30: 30,
    last90: 90
  }
  const days = sizeMap[preset] || 7
  start.setDate(start.getDate() - days + 1)
  return [formatDate(start), formatDate(end)]
}

function getCompareRange(range) {
  const start = new Date(range[0])
  const end = new Date(range[1])
  const diffDays = Math.round((end.getTime() - start.getTime()) / 86400000) + 1
  const compareEnd = new Date(start)
  compareEnd.setDate(compareEnd.getDate() - 1)
  const compareStart = new Date(compareEnd)
  compareStart.setDate(compareStart.getDate() - diffDays + 1)
  return [formatDate(compareStart), formatDate(compareEnd)]
}

function buildDateList(startDate, endDate) {
  const list = []
  const cursor = new Date(startDate)
  const end = new Date(endDate)
  while (cursor <= end) {
    list.push(formatDate(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return list
}

function formatDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getRelativeDate(offsetDays) {
  const date = new Date()
  date.setDate(date.getDate() + offsetDays)
  return date
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return 0
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

function calcRate(value, total) {
  const numerator = toNumber(value)
  const denominator = toNumber(total)
  if (!denominator) return 0
  return numerator / denominator
}

function calcMom(current, previous) {
  const currentValue = toNumber(current)
  const previousValue = toNumber(previous)
  if (!previousValue) {
    if (!currentValue) return 0
    return 1
  }
  return (currentValue - previousValue) / previousValue
}

function formatNumber(value) {
  const num = toNumber(value)
  return new Intl.NumberFormat('zh-CN').format(num)
}

function formatPercent(value) {
  return `${(toNumber(value) * 100).toFixed(2)}%`
}

function formatStayTime(value) {
  const seconds = toNumber(value)
  if (!seconds) return '0s'
  if (seconds < 60) return `${seconds.toFixed(0)}s`
  return `${(seconds / 60).toFixed(2)}m`
}

function formatDeltaText(delta, percent) {
  const deltaValue = toNumber(delta)
  const percentText = formatPercent(percent)
  if (deltaValue === 0) return `持平 ${percentText}`
  return `${deltaValue > 0 ? '+' : ''}${formatNumber(deltaValue)} / ${percentText}`
}

function formatMom(value) {
  const num = toNumber(value)
  if (num === 0) return '环比 0.00%'
  return `环比 ${num > 0 ? '+' : ''}${(num * 100).toFixed(2)}%`
}

function deltaClass(value) {
  const num = toNumber(value)
  if (num > 0) return 'is-up'
  if (num < 0) return 'is-down'
  return 'is-flat'
}

function startExportProgress() {
  clearExportAutoCloseTimer()
  exportProgressVisible.value = true
  exportProgress.status = ''
  exportProgress.current = 0
  exportProgress.percentage = 0
  exportProgress.message = '准备导出...'
  exportProgress.currentLabel = ''
  exportProgress.remaining = 0
  exportProgress.logs = []
  exportProgressCtrl = createExportProgressController(exportProgress)
}

function stepExportProgress(message, increase = false, currentLabel = '') {
  if (!exportProgressCtrl) {
    exportProgressCtrl = createExportProgressController(exportProgress)
  }
  if (increase) {
    exportProgressCtrl.stepDone(message, currentLabel)
  } else {
    exportProgressCtrl.report(message, { label: currentLabel })
  }
}

function finishExportProgress(status, message) {
  if (exportProgressCtrl) {
    exportProgressCtrl.finish(status, message)
  } else {
    exportProgress.percentage = 100
    exportProgress.status = status
    exportProgress.message = message
  }
  if (status === 'success') {
    exportAutoCloseTimer = window.setTimeout(() => {
      exportProgressVisible.value = false
    }, 1600)
  }
}

function closeExportProgressDialog() {
  clearExportAutoCloseTimer()
  exportProgressVisible.value = false
}

function clearExportAutoCloseTimer() {
  if (exportAutoCloseTimer) {
    window.clearTimeout(exportAutoCloseTimer)
    exportAutoCloseTimer = null
  }
}

function clearBehaviorSearchTimer() {
  if (behaviorSearchTimer) {
    window.clearTimeout(behaviorSearchTimer)
    behaviorSearchTimer = null
  }
}

function clearExportBehaviorSearchTimer() {
  if (exportBehaviorSearchTimer) {
    window.clearTimeout(exportBehaviorSearchTimer)
    exportBehaviorSearchTimer = null
  }
}

async function retryLastExport() {
  closeExportProgressDialog()
  if (!lastExportAction.value) return
  await handleExport()
}

function getThemePalette(theme) {
  const palettes = {
    aurora: ['#2563eb', '#14b8a6', '#f97316', '#8b5cf6', '#ef4444', '#0ea5e9'],
    slate: ['#334155', '#0f766e', '#475569', '#0284c7', '#7c3aed', '#dc2626'],
    graphite: ['#111827', '#4b5563', '#1d4ed8', '#0f766e', '#ca8a04', '#7c2d12']
  }
  return palettes[theme] || palettes.aurora
}
</script>

<style scoped>
.export-form-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}

.template-quick-load {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.template-quick-label {
  font-size: 13px;
  color: #475569;
  font-weight: 600;
  white-space: nowrap;
}

.template-option {
  display: flex;
  align-items: center;
}

.summary-row-config {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.summary-row-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  margin-top: 4px;
}

.stay-time-config-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.stay-time-config-block .config-label {
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 6px;
  display: block;
}

.metric-checkbox-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px 12px;
}

/* 渠道+指标一体化配置 */
.channel-metric-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.channel-metric-toolbar-label {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
}

.channel-config-list {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 320px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  scrollbar-width: thin;
}

.channel-config-list::-webkit-scrollbar {
  height: 6px;
  width: 4px;
}

.channel-config-list::-webkit-scrollbar-thumb {
  background: #dbeafe;
  border-radius: 3px;
}

/* 内部用 table 布局保证列对齐 */
.channel-config-table {
  display: table;
  min-width: 100%;
  border-collapse: collapse;
}

.channel-config-row {
  display: table-row;
  transition: background 0.12s;
}

.channel-config-row:not(:last-child) .channel-name-cell,
.channel-config-row:not(:last-child) .channel-metric-cell {
  border-bottom: 1px solid #f1f5f9;
}

.channel-config-row.channel-config-header {
  background: #f8fafc;
}

.channel-config-row.channel-config-header .channel-name-cell,
.channel-config-row.channel-config-header .channel-metric-cell {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  position: sticky;
  top: 0;
  background: #f8fafc;
  z-index: 1;
}

.channel-config-row.is-active {
  background: #f8fbff;
}

/* 渠道名列 */
.channel-name-cell {
  display: table-cell;
  width: 120px;
  min-width: 120px;
  max-width: 160px;
  padding: 8px 12px;
  border-right: 1px solid #e2e8f0;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
}

/* 指标列：内容决定宽度，不换行 */
.channel-metric-cell {
  display: table-cell;
  padding: 8px 12px;
  border-right: 1px solid #f1f5f9;
  vertical-align: middle;
  text-align: center;
  white-space: nowrap;
  font-size: 12px;
  color: #475569;
}

.channel-metric-cell:last-child {
  border-right: none;
}

/* 行为数据渠道横向滚动 */
.channel-scroll-wrap {
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 12px;
  background: #fff;
}

.channel-scroll-wrap::-webkit-scrollbar {
  height: 4px;
}

.channel-scroll-wrap::-webkit-scrollbar-thumb {
  background: #dbeafe;
  border-radius: 2px;
}

.channel-scroll-group {
  display: flex;
  flex-wrap: nowrap;
  gap: 6px 16px;
  white-space: nowrap;
}

/* 停留时长检测区 */
.stay-detect-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 8px 10px;
  border-radius: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  margin-bottom: 8px;
}

.stay-detect-result {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.stay-detect-tip {
  font-size: 12px;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stay-detect-tip strong {
  color: #2563eb;
}

/* 行为选择 & 合并组区域 */
.behavior-selected-panel {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

/* 搜索结果列表：单列，完整显示 */
.behavior-search-result-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  margin-top: 8px;
  width: 100%;
}

.behavior-search-result-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  cursor: pointer;
  transition: background 0.12s;
  user-select: none;
  border-bottom: 1px solid #f1f5f9;
}

.behavior-search-result-item:last-child {
  border-bottom: none;
}

.behavior-search-result-item:hover {
  background: #f8fafc;
}

.behavior-search-result-item.is-selected {
  background: #eff6ff;
}

.behavior-item-name {
  flex: 1;
  font-size: 13px;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  text-align: left;
}

.behavior-item-badge {
  flex-shrink: 0;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: #e2e8f0;
  color: #475569;
}

.behavior-item-badge.merge {
  background: #dbeafe;
  color: #1d4ed8;
}

/* 工具栏 */
.behavior-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 8px 0 4px;
  flex-wrap: wrap;
}

.behavior-toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.behavior-toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.merge-group-action {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 独立行为 tag 区 */
.behavior-tag-area {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px;
  border-radius: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  min-height: 36px;
}

/* 合并组 */
.behavior-sheet-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  flex-wrap: wrap;
}

.behavior-search-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
}

.merged-group-label-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.merged-group-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.merged-group-card {
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #f8fbff;
  padding: 12px;
  transition: border-color 0.15s, background 0.15s;
}

.merged-group-card.is-editing {
  border-color: #f59e0b;
  background: #fffbeb;
}

.merged-group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.merged-group-name-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.merged-group-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.merged-group-badge {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  background: #2563eb;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.merged-group-behaviors {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #dbeafe;
  min-height: 36px;
}
.merged-behavior-group {
  border: 1px solid #EBEEF5;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
  background: #FAFAFA;
}
.merged-behavior-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.export-inline-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}
.export-form-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}
.sheet-config-actions {
  display: flex;
  gap: 4px;
  align-items: center;
}

.report-data-board {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: #f5f7fb;
  min-height: calc(100vh - 120px);
}

.hero-band,
.panel-shell,
.placeholder-shell,
.kpi-card,
.detail-card {
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.hero-band {
  padding: 24px;
}

.hero-main {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-start;
}

.hero-copy h1 {
  margin: 0;
  font-size: 30px;
  color: #111827;
}

.hero-copy p {
  margin: 8px 0 0;
  color: #64748b;
  line-height: 1.6;
}

.hero-meta {
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  color: #64748b;
  font-size: 13px;
}

.project-browser {
  padding: 18px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.sticky-action-bar {
  position: sticky;
  top: 12px;
  z-index: 20;
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) auto auto auto;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #dbe2ea;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.07);
  backdrop-filter: blur(10px);
}

.sticky-project {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
}

.back-project-button {
  padding: 7px 10px;
  font-weight: 600;
  border-color: #93c5fd;
  color: #1d4ed8;
  background: #eff6ff;
}

.sticky-inline-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.sticky-filter-chip {
  min-height: 30px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  display: flex;
  align-items: center;
  gap: 6px;
}

.channel-chip {
  padding-right: 6px;
}

.sticky-filter-chip span {
  color: #475569;
  font-size: 12px;
  font-weight: 600;
}

.sticky-filter-chip small {
  color: #64748b;
  font-size: 12px;
}

.theme-chip {
  padding-right: 6px;
}

.sticky-date {
  flex: 0 0 auto;
}

.sticky-meta-pill {
  padding: 3px 7px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  line-height: 1;
}

.sticky-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.sticky-actions :deep(.el-button) {
  min-height: 30px;
  min-width: 54px;
  padding: 6px 10px;
}

.sticky-date :deep(.el-date-editor.el-input__wrapper),
.sticky-date :deep(.el-date-editor) {
  min-height: 30px;
}

.sticky-filter-chip :deep(.el-select__wrapper) {
  min-height: 26px;
}

.project-toolbar {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.project-card-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.project-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  cursor: pointer;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.project-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 28px rgba(37, 99, 235, 0.08);
  border-color: #bfdbfe;
}

.project-card.is-current {
  border-color: #60a5fa;
  box-shadow: 0 0 0 1px rgba(96, 165, 250, 0.28);
}

.project-card.is-switching {
  border-color: #f59e0b;
  background: linear-gradient(180deg, #fffdf7 0%, #fff7ed 100%);
}

.project-card-head,
.project-card-actions,
.project-card-tags {
  display: flex;
}

.project-card-head {
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.project-card-title h4 {
  margin: 0;
  color: #0f172a;
  font-size: 16px;
  line-height: 1.4;
}

.project-card-title p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 12px;
}

.project-card-tags {
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.project-card-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.project-metric-item {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.project-metric-item span,
.project-card-extra span {
  color: #64748b;
  font-size: 12px;
}

.project-metric-item strong,
.project-card-extra strong {
  color: #0f172a;
  font-size: 13px;
}

.project-card-extra {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.project-card-extra strong {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.project-card-actions {
  gap: 6px;
  flex-wrap: wrap;
}

.project-card-actions :deep(.el-button) {
  min-height: 30px;
  padding: 6px 10px;
}

.project-card-empty {
  padding: 20px 0 4px;
}

.project-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 16px;
}

.kpi-card {
  padding: 16px;
}

.kpi-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  color: #475569;
  font-size: 13px;
}

.kpi-value {
  margin: 14px 0 8px;
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
}

.kpi-compare {
  margin: 0;
  font-size: 13px;
}

.kpi-sub {
  margin: 8px 0 0;
  font-size: 12px;
  color: #94a3b8;
}

.board-tabs :deep(.el-tabs__header) {
  margin-bottom: 16px;
}

.panel-grid {
  display: grid;
  gap: 16px;
}

.two-up {
  grid-template-columns: minmax(0, 1.4fr) minmax(360px, 1fr);
}

.split-grid {
  grid-template-columns: minmax(0, 1.5fr) minmax(320px, 0.9fr);
  margin-top: 16px;
}

.panel-shell {
  padding: 18px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 14px;
}

.panel-header h3 {
  margin: 0;
  font-size: 18px;
  color: #0f172a;
}

.panel-header p {
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
}

.chart-box {
  width: 100%;
}

.large-chart {
  height: 360px;
}

.medium-chart {
  height: 360px;
}

.export-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.summary-item {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.summary-item span {
  font-size: 12px;
  color: #64748b;
}

.summary-item strong {
  color: #0f172a;
}

.table-panel {
  margin-top: 16px;
}

.metric-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.metric-cell small {
  font-size: 12px;
}

.filter-strip {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  align-items: center;
}

.behavior-name-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.behavior-guide-panel {
  display: grid;
  gap: 12px;
}

.guide-item {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  padding: 14px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.guide-item span {
  color: #64748b;
  font-size: 12px;
}

.guide-item strong {
  color: #0f172a;
  font-size: 14px;
}

.guide-tip {
  border-radius: 8px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e3a8a;
  line-height: 1.7;
  padding: 14px;
  font-size: 13px;
}

:deep(.el-table .is-active-top-row) {
  background: #eff6ff !important;
}

:deep(.el-table .is-active-top-row td) {
  background: #eff6ff !important;
}

.behavior-detail-band {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin: 16px 0;
}

.detail-card {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-card span {
  font-size: 12px;
  color: #64748b;
}

.detail-card strong {
  color: #0f172a;
}

.placeholder-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
}

.placeholder-shell {
  padding: 32px;
}

.placeholder-shell h3 {
  margin: 0 0 10px;
  color: #0f172a;
}

.placeholder-shell p,
.placeholder-shell li {
  color: #64748b;
  line-height: 1.7;
}

.export-drawer {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.export-sheet-builder,
.export-sheet-list {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  padding: 16px;
  margin-bottom: 16px;
}

.draft-sheet-actions {
  margin-top: 4px;
}

.sheet-card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sheet-config-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  padding: 14px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.sheet-config-card:hover {
  border-color: #93c5fd;
}

.sheet-config-card.is-editing {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.18);
  background: #eff6ff;
}

.sheet-config-head,
.sheet-config-meta,
.export-search-row,
.sheet-selected-preview {
  display: flex;
}

.sheet-config-head {
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.sheet-config-head strong {
  color: #0f172a;
}

.sheet-config-head p,
.sheet-config-text,
.sheet-config-meta span,
.export-empty-tip {
  color: #64748b;
  font-size: 12px;
}

.sheet-config-head p,
.sheet-config-text {
  margin: 4px 0 0;
}

.sheet-config-meta {
  gap: 12px;
  margin: 10px 0 0;
}

.export-search-row {
  width: 100%;
  gap: 10px;
  align-items: center;
}

.export-search-row :deep(.el-input) {
  flex: 1;
}

.behavior-export-grid {
  max-height: 220px;
  overflow: auto;
  padding-right: 6px;
}

.sheet-selected-preview {
  width: 100%;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 32px;
  padding: 8px 0 2px;
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 12px;
}

.export-preview {
  margin-top: 8px;
  padding: 14px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.export-inline-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 8px;
}

.export-progress-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.export-progress-title {
  color: #0f172a;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.export-progress-meta {
  display: flex;
  justify-content: space-between;
  color: #64748b;
  font-size: 13px;
}

.export-progress-current {
  font-size: 13px;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.export-progress-current strong {
  color: #0f172a;
  font-weight: 600;
}

.export-progress-log {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  overflow: hidden;
}

.export-progress-log-title {
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  border-bottom: 1px solid #e2e8f0;
}

.export-progress-log-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 200px;
  overflow-y: auto;
}

.export-progress-log-list li {
  padding: 8px 12px;
  font-size: 12px;
  color: #334155;
  border-bottom: 1px solid #eef2f7;
  line-height: 1.45;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.export-progress-log-list li.is-done {
  color: #64748b;
}

.export-progress-log-list li.is-done::before {
  content: '✓ ';
  color: #16a34a;
}

.empty-project-shell {
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e5e7eb;
  padding: 32px 16px;
}

.tracking-check-shell {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.tracking-check-head,
.tracking-check-actions,
.tracking-summary-top,
.tracking-summary-values {
  display: flex;
}

.tracking-check-head {
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.tracking-check-head strong {
  color: #0f172a;
  font-size: 16px;
}

.tracking-check-head p {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
}

.tracking-progress-panel,
.tracking-suggestion-shell,
.tracking-other-shell,
.tracking-summary-card {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
}

.tracking-progress-panel,
.tracking-suggestion-shell,
.tracking-other-shell {
  padding: 16px;
}

.tracking-progress-title {
  margin-bottom: 12px;
  color: #0f172a;
  font-weight: 600;
}

.tracking-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.tracking-summary-card {
  padding: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.tracking-summary-top {
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.tracking-summary-values {
  gap: 12px;
  flex-wrap: wrap;
  margin: 14px 0 10px;
  color: #0f172a;
  font-weight: 600;
}

.tracking-summary-card p,
.tracking-suggestion-shell li {
  color: #64748b;
  line-height: 1.7;
}

.tracking-suggestion-shell h4 {
  margin: 0 0 10px;
  color: #0f172a;
}

.tracking-suggestion-shell ul {
  margin: 0;
  padding-left: 18px;
}

.export-preview strong {
  color: #0f172a;
}

.export-preview p {
  margin: 8px 0 0;
  color: #64748b;
  line-height: 1.6;
}

.drawer-footer {
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
}

.table-actions {
  display: flex;
  align-items: center;
}

.is-up {
  color: #059669;
}

.is-down {
  color: #dc2626;
}

.is-flat {
  color: #64748b;
}

.board-fade-enter-active,
.board-fade-leave-active {
  transition: all 0.28s ease;
}

.board-fade-enter-from,
.board-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (max-width: 1280px) {
  .kpi-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .behavior-detail-band,
  .tracking-summary-grid,
  .project-card-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .two-up,
  .split-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 768px) {
  .report-data-board {
    padding: 16px;
  }

  .hero-main {
    flex-direction: column;
  }

  .filter-strip,
  .project-toolbar {
    width: 100%;
  }

  .project-card-grid,
  .project-card-metrics {
    grid-template-columns: minmax(0, 1fr);
  }

  .sticky-action-bar {
    grid-template-columns: minmax(0, 1fr);
    align-items: stretch;
  }

  .sticky-project,
  .sticky-inline-filters {
    width: 100%;
  }

  .sticky-date :deep(.el-date-editor) {
    width: 100% !important;
  }

  .sticky-actions {
    width: 100%;
  }

  .sticky-actions :deep(.el-button) {
    flex: 1;
  }

  .filter-strip :deep(.el-button) {
    flex: 1;
  }

  .kpi-grid,
  .behavior-detail-band,
  .tracking-summary-grid,
  .export-summary,
  .checkbox-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
